import React, { useState, useCallback, useEffect, useMemo, useRef, memo } from 'react';
import {
    Box, Typography, Button, Paper, Chip, IconButton, Tooltip,
    TextField, Checkbox, CircularProgress, Alert, Autocomplete,
    Collapse, Snackbar, Dialog, DialogTitle, DialogContent, DialogActions, InputBase,
} from '@mui/material';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import { filterTags } from './tagZoek';
import SendIcon from '@mui/icons-material/Send';
import RefreshIcon from '@mui/icons-material/Refresh';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import RuleIcon from '@mui/icons-material/Rule';
import HistoryIcon from '@mui/icons-material/History';
import EventIcon from '@mui/icons-material/Event';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { useMsal } from '@azure/msal-react';
import toast from 'react-hot-toast';
import { bevestig } from '../../utils/bevestig';
import { authFetch } from '../../utils/authHelpers';
import { leesJson, meldFout } from '../../utils/apiFout';
import { acquireGraphToken } from '../../utils/graphToken';
import { haalAgendaOp, agendaFoutmelding } from './agenda';
import { BACKEND_URL } from '../../utils/apiConfig';
import PageHeader from '../../ui-components/PageHeader';
import { KwaliteitTegel, MijnRegels } from './SlimUrenLeren';
import MailBronnen from './MailBronnen';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

// ── Helpers ────────────────────────────────────────────────────────────────

const DAY_NAMES_FULL = ['Zondag', 'Maandag', 'Dinsdag', 'Woensdag', 'Donderdag', 'Vrijdag', 'Zaterdag'];
const MONTH_NAMES = ['jan', 'feb', 'mrt', 'apr', 'mei', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'];

const TYPE_EMOJI = {
    'Montage': '🎬', 'Animatie': '✨', 'Preproductie': '📋', 'Draaidag': '🎥',
    'Regie': '🎯', 'Script': '📝', 'Afspraak': '📅', 'Meeting': '👥',
    'Cursus': '📚', 'Overleg': '💬', 'Admin': '📑',
};

// Tasks to filter out — not real work items
// Tasks to filter out — not real work items (check name, priority AND type)
const SKIP_NAMES = new Set(['vanallesuurtje', 'vrij/intern']);
const SKIP_TYPES = new Set(['vanallesuurtje', 'vrij/intern']);
const INTERNAL_LINE_PRIORITY = [
    'afspraken', 'dagstart', 'boekhouding', 'planning', 'sales', 'marketing',
    'team overleg', 'mt overleg', 'coaching', 'offerte', 'cursus',
];

/**
 * Hoe vaak koos jij een bepaalde regel? We tellen op naam, niet op id: "Editing"
 * heeft in elk project een ander id, maar het is voor jou steeds dezelfde keuze.
 * Staat in deze browser; leeg = we vallen terug op de standaardvolgorde.
 */
const KEUZE_SLEUTEL = 'slim-uren-lijnkeuzes';

function leesKeuzes() {
    try {
        const ruw = localStorage.getItem(KEUZE_SLEUTEL);
        return ruw ? JSON.parse(ruw) : {};
    } catch { return {}; }
}

function onthoudKeuze(naam) {
    const sleutel = String(naam || '').trim().toLowerCase();
    if (!sleutel) return;
    try {
        const tellers = leesKeuzes();
        tellers[sleutel] = (tellers[sleutel] || 0) + 1;
        localStorage.setItem(KEUZE_SLEUTEL, JSON.stringify(tellers));
    } catch { /* privémodus: dan zonder voorkeur */ }
}

/** Jouw meestgekozen eerst; bij gelijke stand blijft de bestaande volgorde staan. */
// Rolvoorkeur (Joris, 16 sep): een PL wil 'Projectbegeleiding en communicatie' bovenaan, een
// editor 'Monteren'. Gezet vanuit de personenspiegel (functie) zodra de pagina laadt.
let ROL_PRIOR = [];
const ROL_PRIOR_PER_FUNCTIE = [
    [/projectleider|accountmanager|producer/i, [/projectbegeleiding/i, /communicatie/i]],
    [/editor|monteur/i, [/monteren|montage/i]],
    [/animator|illustrator/i, [/animeren|animatie|illustr/i]],
    [/cameraman|camera operator|dop/i, [/cameraman|camera|opname/i]],
    [/regisseur/i, [/regisseur|regie/i]],
];
export function zetRolPrior(functie) {
    const f = String(functie || '');
    ROL_PRIOR = ROL_PRIOR_PER_FUNCTIE.filter(([re]) => re.test(f)).flatMap(([, prior]) => prior);
}
const rolScore = (naam) => { const i = ROL_PRIOR.findIndex((re) => re.test(naam || '')); return i < 0 ? 0 : ROL_PRIOR.length - i; };
function opVoorkeur(opties, naamVan) {
    const tellers = leesKeuzes();
    return opties
        .map((o, i) => { const naam = String(naamVan(o) || '').trim(); return { o, i, n: tellers[naam.toLowerCase()] || 0, r: rolScore(naam) }; })
        .sort((a, b) => (b.r - a.r) || (b.n - a.n) || (a.i - b.i))
        .map((x) => x.o);
}

/**
 * Keuzelijst als tags. Toont de eerste negen en pas op verzoek de rest, zodat een
 * project met dertig regels het scherm niet vult. De gekozen tag staat altijd
 * tussen de zichtbare, ook als hij verderop in de lijst zit. Bij een lange lijst
 * staat er een zoekveldje aan het eind van de rij: typen filtert over álle tags,
 * Enter kiest de eerste treffer (wiki-feedback Slim Uren 26 sep).
 */
export function TagKeuze({ opties, actiefId, onKies, kleur, zichtbaar = 9 }) {
    const [alles, setAlles] = useState(false);
    const [zoek, setZoek] = useState('');
    // Andere lijst (projectwissel): zoekterm leeg, anders blijft een onzichtbaar filter hangen.
    const lijstSleutel = opties.map((o) => String(o.id)).sort().join('|');
    useEffect(() => { setZoek(''); }, [lijstSleutel]);
    if (!opties.length) return null;
    const zoekend = zoek.trim().length > 0;
    const treffers = zoekend ? filterTags(opties, zoek) : null;
    const actiefIndex = opties.findIndex((o) => o.id === actiefId);
    const eerste = opties.slice(0, zichtbaar);
    if (actiefIndex >= zichtbaar) eerste[zichtbaar - 1] = opties[actiefIndex];
    const toon = zoekend ? treffers : (alles ? opties : eerste);
    const rest = opties.length - eerste.length;
    const kies = (o) => { setZoek(''); onKies(o); };

    return (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, alignItems: 'center' }}>
            {toon.map((o) => {
                const actief = o.id === actiefId;
                return (
                    <Chip key={o.id}
                        label={o.label}
                        clickable
                        onClick={() => kies(o)}
                        size="small"
                        variant={actief ? 'filled' : 'outlined'}
                        sx={{
                            height: 24, fontSize: 11, fontWeight: 600, maxWidth: 260,
                            bgcolor: actief ? kleur.bg : '#fff',
                            color: actief ? kleur.fg : 'text.secondary',
                            borderColor: actief ? kleur.rand : 'divider',
                        }} />
                );
            })}
            {zoekend && !treffers.length && (
                <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>Geen tag met “{zoek.trim()}”</Typography>
            )}
            {!zoekend && rest > 0 && (
                <Chip
                    label={alles ? 'Minder' : `Meer (${rest})`}
                    clickable
                    onClick={() => setAlles(!alles)}
                    size="small"
                    variant="outlined"
                    sx={{ height: 24, fontSize: 11, fontWeight: 700, color: 'text.secondary', borderStyle: 'dashed' }} />
            )}
            {(rest > 0 || zoek) && (
                <InputBase value={zoek} onChange={(e) => setZoek(e.target.value)} placeholder="Zoek…"
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' && treffers?.length) { e.preventDefault(); kies(treffers[0]); }
                        if (e.key === 'Escape') setZoek('');
                    }}
                    inputProps={{ 'aria-label': 'Zoek in de tags' }}
                    sx={{ height: 24, width: 110, px: 1, fontSize: 11, border: '1px solid', borderColor: 'divider', borderRadius: 3,
                        '&.Mui-focused': { borderColor: kleur.rand } }} />
            )}
        </Box>
    );
}

/**
 * Urenveld. Een gewoon number-veld liet "01,5" staan: de browser houdt zijn eigen
 * tekst vast zolang die (voor hem) geldig is, en een komma parst niet. Dit veld
 * accepteert komma én punt, en zet bij het verlaten netjes om naar 1,5.
 */
function UrenVeld({ waarde, onChange }) {
    const [tekst, setTekst] = useState(() => String(waarde ?? 0).replace('.', ','));
    const [bezig, setBezig] = useState(false);

    useEffect(() => {
        if (!bezig) setTekst(String(waarde ?? 0).replace('.', ','));
    }, [waarde, bezig]);

    const naarGetal = (t) => {
        const n = parseFloat(String(t).replace(',', '.'));
        if (!Number.isFinite(n)) return 0;
        return Math.min(24, Math.max(0, n));
    };

    return (
        <TextField
            value={tekst}
            inputMode="decimal"
            onFocus={() => setBezig(true)}
            onChange={(e) => {
                const schoon = e.target.value.replace(/[^\d.,]/g, '').slice(0, 6);
                setTekst(schoon);
                onChange(naarGetal(schoon));
            }}
            onBlur={() => {
                setBezig(false);
                const n = naarGetal(tekst);
                setTekst(String(n).replace('.', ','));
                onChange(n);
            }}
            inputProps={{ style: { textAlign: 'center', padding: '4px 0' }, 'aria-label': 'Aantal uren' }}
            variant="standard" sx={{ width: 50, '& input': { fontWeight: 700, fontSize: 14 } }} />
    );
}

/**
 * Losse 4-cijferige nummers (mogelijk een projectnummer) uit het begin van een agenda- of mailtekst. Alleen
 * de nummers gaan naar de server, nooit de tekst; de engine controleert ze tegen de projectspiegel (11 okt).
 * Geen deel van een langer getal/datum/bedrag, geen postcode ("9838 AB"), en < 7000 telt niet.
 */
/** Offertesignaal uit de eerste regels van een mail/afspraak: alleen ja/nee en de nummers direct achter 'offerte'
 *  gaan naar de server, geen tekst (20 okt 2026). */
export function offerteUitTekst(tekst, context = '') {
    const t = String(tekst || '').slice(0, 600);
    // `context` = het onderwerp/de titel: een offertesignaal dáár laat '#2026' in de tekst ook meetellen (Codex r9 #1).
    const signaal = /\b(offerte\w*|quotation|quote|proposal|prijsopgave|statement of work)\b/i;
    const offerteSignaal = signaal.test(t) || signaal.test(String(context || ''));
    // Geen bedrag ('9856,50') en geen ongemarkeerd jaartal ('offerteaanvraag 2026'); met 'nr.'/'nummer'/'#' wel.
    const offerteNummers = [...t.matchAll(/\boffert\w*\s*(?:nr\.?|nummer|no\.?)?\s*[:#]?\s*\[?(\d{4,5})\b(?![.,]\d)/gi)]
        .filter((m) => !(Number(m[1]) >= 1900 && Number(m[1]) <= 2099) || /nr\.?|nummer|\bno\b\.?|#|:/i.test(m[0]))
        .map((m) => m[1]);
    // '#9856' bij een offertesignaal is ook een gemarkeerd offertenummer (zelfde regel als de engine, Codex r8 #1).
    if (offerteSignaal) for (const m of t.matchAll(/(?<![\w.,/:€$-])#\[?(\d{4,5})\]?(?![\w/€]|[.,:]\d)/g)) offerteNummers.push(m[1]);
    return { offerteSignaal, offerteNummers: [...new Set(offerteNummers)].slice(0, 5) };
}

export function nummersUitTekst(tekst, max = 5) {
    const t = String(tekst || '').slice(0, 400);
    const uit = [];
    const re = /(?<![\w.,/:€$-])#?\[?(\d{4})\]?(?![\w/€]|[.,:]\d)/g;
    let m;
    while ((m = re.exec(t)) && uit.length < max) {
        const na = t.slice(m.index + m[0].indexOf(m[1]) + 4);
        if (/^\s?[A-Z]{2}(?![\w:])/.test(na)) continue;   // postcode
        if (Number(m[1]) >= 7000 && !uit.includes(m[1])) uit.push(m[1]);
    }
    return uit;
}

function getMonday(weeksAgo = 0) {
    const d = new Date();
    const day = d.getDay();
    d.setDate(d.getDate() - (day === 0 ? 6 : day - 1) - weeksAgo * 7);
    return d.toISOString().split('T')[0];
}

function getWeekNumber(dateStr) {
    // ISO 8601 weeknummer — voorkomt off-by-one rond de jaarwisseling.
    const d = new Date(dateStr + 'T12:00:00Z');
    const target = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
    const dayNum = target.getUTCDay() || 7; // ma=1 … zo=7
    target.setUTCDate(target.getUTCDate() + 4 - dayNum); // donderdag van deze week
    const yearStart = new Date(Date.UTC(target.getUTCFullYear(), 0, 1));
    return Math.ceil(((target - yearStart) / 86400000 + 1) / 7);
}

function formatDay(dateStr) {
    const d = new Date(dateStr + 'T12:00:00Z');
    return `${DAY_NAMES_FULL[d.getUTCDay()]} ${d.getUTCDate()} ${MONTH_NAMES[d.getUTCMonth()]}`;
}

function formatDayShort(dateStr) {
    const d = new Date(dateStr + 'T12:00:00Z');
    return `${DAY_NAMES_FULL[d.getUTCDay()].substring(0, 2).toUpperCase()} ${d.getUTCDate()} ${MONTH_NAMES[d.getUTCMonth()].toUpperCase()}`;
}

function getWeekDays(mondayStr) {
    const days = [];
    const monday = new Date(mondayStr + 'T12:00:00Z');
    for (let i = 0; i < 5; i++) {
        const d = new Date(monday);
        d.setUTCDate(monday.getUTCDate() + i);
        days.push(d.toISOString().split('T')[0]);
    }
    return days;
}

function getTypeEmoji(type) {
    if (!type) return '📌';
    for (const [key, emoji] of Object.entries(TYPE_EMOJI)) {
        if (type.toLowerCase().includes(key.toLowerCase())) return emoji;
    }
    return '📌';
}

// ── Source badge ────────────────────────────────────────────────────────────

function SourceBadge({ source, geleerd }) {
    if (geleerd) return (
        <Tooltip title={geleerd}><HistoryIcon sx={{ fontSize: 14, color: '#526075', opacity: 0.7 }} /></Tooltip>
    );
    if (source === 'auto_rule') return (
        <Tooltip title="Automatische regel"><RuleIcon sx={{ fontSize: 14, color: '#526075', opacity: 0.6 }} /></Tooltip>
    );
    if (source === 'ai_estimate') return (
        <Tooltip title="AI suggestie"><SmartToyIcon sx={{ fontSize: 14, color: '#635983', opacity: 0.7 }} /></Tooltip>
    );
    if (source === 'mail') return (
        <Tooltip title="Uit je verzonden mail"><span style={{ fontSize: 13, opacity: 0.8 }}>📧</span></Tooltip>
    );
    return null;
}

// ── Agenda item (left column) ──────────────────────────────────────────────

// Breedte van de agendakolom: 340px als ondergrens (zoals altijd), op brede schermen meegroeiend tot 460px
// zodat titels minder snel afbreken. Nooit smaller dan voorheen en nooit meer dan 32% (of 340px op smallere
// schermen), zodat de Gripp-kolom niet in de knel komt (Joris, 23 okt 2026). Kop, rijen en toevoegregel delen
// deze ene waarde, dus de agenda- en Gripp-cellen blijven exact onder elkaar staan.
export const AGENDA_KOLOM = 'clamp(340px, 32%, 460px)';

// Titel: hooguit twee regels, daarna "…". De volledige titel staat in de tooltip én in de DOM (de clamp is
// alleen visueel), dus schermlezers lezen hem altijd helemaal voor.
const TITEL_CLAMP = {
    display: '-webkit-box', WebkitBoxOrient: 'vertical', WebkitLineClamp: 2, lineClamp: 2,
    overflow: 'hidden', overflowWrap: 'anywhere',
};

export function AgendaItem({ task }) {
    const isOutlook = task.source === 'outlook';
    const emoji = isOutlook ? '📅' : getTypeEmoji(task.type);
    const naam = task.name || 'Naamloze taak';
    const deelnemers = (task.attendees || []).join(', ');
    // Deelnemers: één regel; klik klapt de hele lijst uit (en weer in).
    const [metOpen, setMetOpen] = useState(false);
    const wisselMet = () => setMetOpen((v) => !v);
    return (
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, py: 0.5, width: '100%' }}>
            <Typography sx={{ fontSize: 13, lineHeight: '18px', flexShrink: 0 }}>{emoji}</Typography>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Tooltip title={naam} enterDelay={500} enterNextDelay={300} placement="top-start">
                    <Typography variant="body2" data-testid="agenda-titel" tabIndex={0}
                        sx={{ fontWeight: 500, fontSize: 12.5, lineHeight: '18px', ...TITEL_CLAMP,
                            '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 1, borderRadius: 0.5 } }}>
                        {naam}
                    </Typography>
                </Tooltip>
                <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center', mt: 0.25, flexWrap: 'wrap' }}>
                    {isOutlook && task.startTime && (
                        <Typography variant="caption" sx={{ fontSize: 10, color: '#526075', fontWeight: 600 }}>
                            {task.startTime}–{task.endTime}
                        </Typography>
                    )}
                    {task.hours > 0 && (
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
                            {task.hours}h
                        </Typography>
                    )}
                    {task.projectName && (
                        <Typography variant="caption" color="text.disabled" noWrap sx={{ fontSize: 10 }}>
                            {task.projectNumber ? `${task.projectNumber} ` : ''}{task.projectName}
                        </Typography>
                    )}
                    {task.type && !isOutlook && (
                        <Chip label={task.type} size="small"
                            sx={{ height: 14, fontSize: 9, bgcolor: '#f1f4f5', '& .MuiChip-label': { px: 0.5 } }} />
                    )}
                    {isOutlook && (
                        <Chip label="Outlook" size="small"
                            sx={{ height: 14, fontSize: 9, bgcolor: '#e4ecf5', color: '#3b6ea5', '& .MuiChip-label': { px: 0.5 } }} />
                    )}
                </Box>
                {deelnemers && (
                    <Tooltip title={metOpen ? '' : `met ${deelnemers}`} enterDelay={500} enterNextDelay={300} placement="bottom-start">
                        <Typography variant="caption" noWrap={!metOpen} data-testid="agenda-deelnemers"
                            role="button" tabIndex={0} aria-expanded={metOpen}
                            onClick={wisselMet}
                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); wisselMet(); } }}
                            sx={{ display: 'block', fontSize: 10, color: 'text.disabled', mt: 0.15, cursor: 'pointer',
                                overflowWrap: metOpen ? 'anywhere' : undefined, '&:hover': { color: 'text.secondary' } }}>
                            met {deelnemers}
                        </Typography>
                    </Tooltip>
                )}
            </Box>
        </Box>
    );
}

// ── "+ uren" bij een afspraak zonder voorstel (Joris, 11 okt) ───────────────
// "Waarom kan ik hier niet meer uren achter zetten bij sommige items?" Een afspraak zonder voorstel kreeg
// alleen een grijs streepje. Nu: een knop "+ uren" die een regel aan die afspraak hangt (duur, titel, beste
// projectgok). Zo'n regel gaat als 'gemist' de leerlus in: hier had de AI iets moeten voorstellen.
// Het eerste woord van de klantnaam is het kenmerk ("UMC Utrecht" → umc). Begint de naam met een
// organisatievorm ("Gemeente Utrecht"), dan is er geen kenmerk: een plaatsnaam is geen klant.
const LIDWOORD = new Set(['de', 'het', 'een', 'the']);
const GEEN_KLANTWOORD = new Set(['gemeente', 'stichting', 'provincie', 'waterschap', 'ministerie', 'vereniging', 'bv', 'nv',
    'groep', 'group', 'holding', 'nederland', 'full', 'frame', 'fullframe']);
const woordenVan = (t) => String(t || '').toLowerCase().split(/[^a-z0-9à-ÿ]+/).filter(Boolean);

/** Beste projectgok voor "+ uren": de hint van de server (eigen regel/correctie/projectnummer/klantnaam) →
 *  het project van de taak → het eerste kenmerkende klantwoord van een van je recente projecten ("UMC"). */
export function gokProject(task, hint, projectOptions = [], internProject = null) {
    if (hint?.project) {
        const intern = hint.project === 'Intern' || Boolean(internProject && String(hint.project) === String(internProject.projectId));
        return {
            intern, projectNumber: intern ? 'Intern' : String(hint.project),
            projectName: intern ? 'Intern' : (hint.projectNaam || `Project ${hint.project}`),
            regelId: hint.regelId ?? null, regelNaam: hint.regelNaam || '', regelTrefwoord: hint.regelTrefwoord || '', reden: hint.reden || '',
        };
    }
    if (task?.projectNumber) {
        return { intern: false, projectNumber: String(task.projectNumber), projectName: task.projectName || `Project ${task.projectNumber}`, reden: 'het project van de taak' };
    }
    const titel = new Set(woordenVan(task?.name).filter((w) => w.length >= 3));
    if (!titel.size) return null;
    const treffer = (projectOptions || []).find((p) => {
        if (p.isInternal || p.archived || !p.companyName) return false;
        const eerste = woordenVan(p.companyName).find((w) => !LIDWOORD.has(w));
        return Boolean(eerste) && eerste.length >= 3 && !GEEN_KLANTWOORD.has(eerste) && titel.has(eerste);
    });
    if (!treffer) return null;
    return {
        intern: false, projectNumber: String(treffer.projectNumber || treffer.projectId),
        lookupId: String(treffer.lookupProjectId || treffer.projectNumber || treffer.projectId),
        projectName: treffer.projectName || `Project ${treffer.projectNumber}`,
        reden: `klant ${treffer.companyName} uit je recente projecten`,
    };
}

const regelNaam = (l) => (l && (l.name || l.searchname || l.additionalsubject)) || '';

// ── Offertes als doel (besluit Joris, 20 okt 2026) ──────────────────────────
// Uren voor een offerte gaan op die offerte, regel "Projectbegeleiding en communicatie". Een offerte loopt door
// de hele keten als `offerte:<gripp_id>` en nooit als nummer: offerte- en projectnummers vallen samen zodra een
// offerte is omgezet, en het nummer laadt altijd eerst het project.
// ── Projectkiezer: actief = de afgelopen 18 maanden (Joris, 23 okt 2026) ──────
// Gripp archiveert lang niet alles: "Live registratie UMC 7 februari 2013 (1073)" stond tussen 9688/9689/9690
// onder "Actieve zoekresultaten". Actief is nu: einddatum in de toekomst, of een start/mutatie/aanmaakdatum
// binnen ACTIEF_MAANDEN. De rest komt onder de kop "Ouder", alleen met "Archief tonen" aan. Je eigen recente
// projecten ("Recent gebruikt") en de open offertes (offerte:<id>) vallen hier buiten en blijven altijd staan.
export const ACTIEF_MAANDEN = 18;
const GROEP_OUDER = 'Ouder';

/** Gripp-datum ({ date: 'YYYY-MM-DD hh:mm:ss' } of string) → Date, of null. */
export function grippDatum(v) {
    const tekst = v && typeof v === 'object' ? v.date : v;
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(tekst || ''));
    return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : null;
}

/** Recent genoeg om tussen de actieve zoekresultaten te staan? Zonder enige datum: ja (niet stil verbergen). */
export function isRecentActief(p, nu = new Date(), maanden = ACTIEF_MAANDEN) {
    const eind = grippDatum(p?.enddate);
    if (eind && eind >= new Date(nu.getFullYear(), nu.getMonth(), nu.getDate())) return true;
    const datums = ['updatedon', 'createdon', 'startdate', 'enddate', 'deadline', 'deliverydate', 'date']
        .map((k) => grippDatum(p?.[k])).filter(Boolean);
    if (!datums.length) return true;
    // Zelfde dag N maanden terug, begrensd op de laatste dag van die maand (31 okt → 30 apr, niet 1 mei).
    const doel = new Date(nu.getFullYear(), nu.getMonth() - maanden, 1);
    const laatsteDag = new Date(doel.getFullYear(), doel.getMonth() + 1, 0).getDate();
    const grens = new Date(doel.getFullYear(), doel.getMonth(), Math.min(nu.getDate(), laatsteDag));
    return Math.max(...datums.map((d) => d.getTime())) >= grens.getTime();
}

/** Ruwe Gripp-zoekrijen + open offertes → opties voor de kiezer, per kop gesorteerd (actief, offertes, ouder). */
export function zoekOpties(rijen, offertes, includeArchived, nu = new Date()) {
    const actief = [];
    const ouder = [];
    for (const p of rijen || []) {
        const companyName = p.company?.searchname || p.client?.searchname || '';
        const projectName = p.searchname || p.additionalsubject || `Project ${p.id}`;
        // Gripp levert `archived` (bool) + `phase` { id, searchname }; het oude `status`
        // veld is niet beschikbaar in deze response.
        const statusLabel = p.archived ? 'Archief' : (p.phase?.searchname || 'Lopend');
        const status = statusLabel.toLowerCase();
        const archived = !!p.archived || status === 'archief' || status === 'gearchiveerd';
        const oud = archived || !isRecentActief(p, nu);
        const optie = {
            projectId: p.id,
            projectNumber: p.number || p.id,
            projectName,
            companyName,
            label: companyName ? `${projectName} — ${companyName}` : projectName,
            statusLabel,
            archived,
            ouder: oud,
            sourceGroup: oud ? GROEP_OUDER : (includeArchived ? 'Zoekresultaten' : 'Actieve zoekresultaten'),
        };
        (oud ? ouder : actief).push(optie);
    }
    return [...actief, ...(offertes || []), ...(includeArchived ? ouder : [])];
}

export const isOfferteSleutel = (pn) => /^offerte:\d{1,10}$/.test(String(pn || ''));
export const regelsUrl = (pn) => `${BACKEND_URL}/Api/slim-uren/project-lines/${encodeURIComponent(String(pn))}`;
/** De regel voor offerte-uren: Projectbegeleiding en communicatie, of wat daar het dichtst bij komt (zelfde volgorde als de engine). */
export function offerteRegel(lines) {
    const lijst = (lines || []).filter((l) => !/groep/i.test(regelNaam(l)));
    for (const re of [/projectbegeleiding/i, /communicatie/i, /begeleiding/i, /overleg|afstemming|contact|accountmanagement/i]) {
        const r = lijst.find((l) => re.test(regelNaam(l)));
        if (r) return r;
    }
    return null;
}
/** Projectkiezer-optie voor een offerte (eigen label "Offerte 1234 – titel"). */
export function offerteOptie({ projectNumber, projectName, companyName = '', sourceGroup = 'Offertes', offerteNummer = '' }) {
    return {
        projectId: projectNumber, projectNumber, projectName: projectName || 'Offerte', companyName, offerteNummer,
        label: companyName ? `${projectName} — ${companyName}` : (projectName || 'Offerte'),
        statusLabel: 'Offerte', isOfferte: true, archived: false, sourceGroup,
    };
}

/** Projectlijn bij de gok: de lijn uit de hint, anders (extern) begeleiding/overleg of je rolvoorkeur, (intern) Afspraken. */
export function kiesRegel(lines, gok) {
    const lijst = (lines || []).filter((l) => !/groep/i.test(regelNaam(l)));
    if (!lijst.length || !gok) return null;
    if (gok.regelId !== null && gok.regelId !== undefined) {
        const r = lijst.find((l) => Number(l.id) === Number(gok.regelId));
        if (r) return r;
    }
    if (gok.regelNaam) { const r = lijst.find((l) => regelNaam(l) === gok.regelNaam); if (r) return r; }
    if (gok.regelTrefwoord) {
        const r = lijst.find((l) => regelNaam(l).toLowerCase().includes(gok.regelTrefwoord.toLowerCase()));
        if (r) return r;
    }
    if (gok.intern) return lijst.find((l) => /afspraken|overleg/i.test(regelNaam(l))) || null;
    if (isOfferteSleutel(gok.projectNumber)) return offerteRegel(lijst);
    return lijst.find((l) => /projectbegeleiding|communicatie|overleg|afstemming/i.test(regelNaam(l)))
        || opVoorkeur(lijst, regelNaam)[0] || null;
}

/** Waarom staat er geen voorstel bij deze afspraak? Alleen als de server iets weet (anders null). */
export function redenZonderVoorstel(task, data, daySuggestions = []) {
    const weg = (data?.weggefilterd || []).find((w) => w.id && w.id === task?.id);
    if (weg) return `Geen voorstel: overgeslagen (${weg.reden}).`;
    if (!data?.agendaHints) return null;   // werkstaat van vóór 11 okt: reden onbekend
    const h = data.agendaHints[task?.id];
    if (h) {
        const naam = h.project === 'Intern' ? 'Intern' : (h.projectNaam || h.project);
        const zelfde = daySuggestions.find((x) => String(x.projectNumber) === String(h.project) && x.taskId !== task.id);
        if (zelfde) return `Geen apart voorstel: waarschijnlijk samengevoegd met “${zelfde.description || zelfde.projectLineName || naam}”.`;
        return `Project herkend (${naam}${h.reden ? ` — ${h.reden}` : ''}), maar de AI deed hier geen voorstel.`;
    }
    return 'Geen voorstel: geen project herkend aan de titel of de deelnemers, en de AI zag het niet als aparte werkregel.';
}

/** Kleur van het dagtotaal: groen vanaf 95% van de dagnorm, oranje eronder, rood erboven. */
export function dagKleur(totaal, norm) {
    if (totaal > norm + 0.001) return '#b91c1c';
    if (totaal >= norm * 0.95) return '#166534';
    return '#b45309';
}

const rond = (n) => Math.round((Number(n) || 0) * 100) / 100;
const komma = (n) => String(rond(n)).replace('.', ',');

/** Max uren op een dag zodra er interne uren bij zitten; alleen puur externe uren mogen erboven. */
export const DAGGRENS_INTERN = 8;

/**
 * Agenda-kolom: Outlook-afspraken van de browser bij de items van de server voegen. De server stuurt een
 * Airtable-afspraak die bij een Outlook-afspraak hoort al samengevoegd terug, als het Outlook-item met hetzelfde
 * id (okt 2026, Skinergy 13 okt) — op id dus niet nog eens. Op naam alleen tegen taken: twee Outlook-afspraken
 * met dezelfde titel op één dag (ochtend + middag) blijven er allebei.
 */
export function voegAgendaSamen(tasksByDay, calEvents) {
    const uit = { ...(tasksByDay || {}) };
    for (const [day, events] of Object.entries(calEvents || {})) {
        const bestaand = uit[day] || [];
        const ids = new Set(bestaand.map((t) => t.id));
        const taakNamen = new Set(bestaand.filter((t) => t.source !== 'outlook').map((t) => (t.name || '').toLowerCase()));
        const erbij = (events || [])
            .filter((ev) => !ev.isAllDay && ev.showAs !== 'free')
            .filter((ev) => !ids.has(ev.id) && !taakNamen.has((ev.name || '').toLowerCase()))
            .map((ev) => ({ ...ev, projectNumber: '', projectName: '' }));
        uit[day] = [...bestaand, ...erbij];
    }
    return uit;
}

/**
 * Dagen boven de daggrens mét interne uren: [{ day, totaal, intern }]. `regels` = wat geschreven gaat worden,
 * `bestaand` = al geschreven Gripp-uren (tellen alleen mee op dagen waar regels bij komen).
 */
export function dagGrensOvertredingen(regels, bestaand, isInternRegel, grens = DAGGRENS_INTERN) {
    const perDag = {};
    const tel = (x, d) => {
        const u = Number(x.hours) || 0;
        const v = perDag[d];
        v.totaal += u;
        if (isInternRegel(x)) { v.metIntern = true; v.intern += u; }
    };
    for (const r of regels || []) {
        const d = String(r.date || '').slice(0, 10);
        if (!d) continue;
        perDag[d] = perDag[d] || { totaal: 0, intern: 0, metIntern: false };
        tel(r, d);
    }
    for (const e of bestaand || []) {
        const d = String(e.date || '').slice(0, 10);
        if (perDag[d]) tel(e, d);
    }
    return Object.entries(perDag)
        .filter(([, v]) => v.totaal > grens + 0.001 && v.metIntern)
        .map(([day, v]) => ({ day, totaal: rond(v.totaal), intern: rond(v.intern) }))
        .sort((a, b) => a.day.localeCompare(b.day));
}

/**
 * Welke voorstellen na het schrijven als geschreven tellen: de regels die de server als gelukt meldt (op index
 * in de aanvraag) plus de regels die al in Gripp stonden (`overgeslagen`). Zonder index-informatie (oude server):
 * alles bij succes, niets bij een fout.
 */
export function geschrevenIds(toWrite, result) {
    const metIndex = [...(result?.results || []), ...(result?.errors || []), ...(result?.overgeslagen || [])]
        .some((r) => Number.isInteger(r?.index));
    if (!metIndex) return new Set(result?.success && !(result?.failed > 0) ? toWrite.map((x) => x.id) : []);
    const ok = new Set([...(result.results || []).filter((r) => r.success !== false), ...(result.overgeslagen || [])]
        .map((r) => r.index).filter(Number.isInteger));
    return new Set(toWrite.filter((_, i) => ok.has(i)).map((x) => x.id));
}

const regelSleutel = (dag, regel, uren, tekst) => [String(dag || '').slice(0, 10), Number(regel), Math.round((Number(uren) || 0) * 100),
    String(tekst || '').split(/\s+/).filter(Boolean).join(' ').toLowerCase()].join('|');

/**
 * Open regels die al als Gripp-uur in de geladen uren staan (zelfde dag, regel, uren, omschrijving; 1-op-1) —
 * dezelfde regel als de server. Die gaan niet mee in de daggrens-precheck (de server slaat ze over). → Set van ids.
 */
export function alGeboekt(regels, bestaand) {
    const voorraad = new Map();
    for (const e of bestaand || []) {
        const k = regelSleutel(e.date, e.projectLineId, e.hours, e.description);
        voorraad.set(k, (voorraad.get(k) || 0) + 1);
    }
    const uit = new Set();
    for (const r of regels || []) {
        const k = regelSleutel(r.date, r.projectLineId, r.hours, r.description);
        if (voorraad.get(k) > 0) { voorraad.set(k, voorraad.get(k) - 1); uit.add(r.id); }
    }
    return uit;
}

/**
 * In deze sessie geschreven regels die nog niet in de geladen Gripp-uren zitten: elke Gripp-boeking telt één keer
 * (uniek op hourlogId; staat die id al in existingEntries, dan telt hij daar).
 */
export function sessieGeschrevenRegels(suggestions, existingEntries) {
    const geladen = new Set((existingEntries || []).map((e) => String(e.id)));
    const gezien = new Set();
    return (suggestions || []).filter((x) => {
        if (!x.written || !x.approved || !((Number(x.hours) || 0) > 0)) return false;
        if (x.hourlogId == null) return true;
        const id = String(x.hourlogId);
        if (geladen.has(id) || gezien.has(id)) return false;
        gezien.add(id);
        return true;
    });
}

/**
 * Totalen van één dag. "Nieuw" = aangevinkt en nog niet geschreven (week 42: geschreven regels telden nog als
 * "+8h nieuw"); het dagtotaal = al in Gripp (bij het laden) + in deze sessie geschreven + nieuw.
 */
export function dagSamenvatting(daySuggestions, existingEntries, day, vrijeDag = false) {
    const aan = daySuggestions.filter((x) => x.approved && (Number(x.hours) || 0) > 0);
    const dayTotal = rond(aan.filter((x) => !x.written).reduce((sum, x) => sum + (Number(x.hours) || 0), 0));
    const sessieGeschreven = rond(sessieGeschrevenRegels(daySuggestions, existingEntries).reduce((sum, x) => sum + (Number(x.hours) || 0), 0));
    const dayExisting = rond((existingEntries || [])
        .filter((h) => (h.date?.substring?.(0, 10) || h.date) === day)
        .reduce((sum, h) => sum + (Number(h.hours) || 0), 0));
    return {
        dayTotal, dayExisting, sessieGeschreven, totaal: rond(dayTotal + sessieGeschreven + dayExisting), vrijeDag,
        schrijfbaar: aan.filter((x) => x.projectLineId && !x.written).length,
        allesGeschreven: daySuggestions.length > 0 && daySuggestions.every((x) => x.written || !x.approved),
    };
}

/** Hoeveel interne uren er minstens af moeten: tot de daggrens, of alles intern weg (dan is het puur extern). */
export function teVeelIntern(o, grens = DAGGRENS_INTERN) {
    return rond(Math.min(o.intern, o.totaal - grens));
}

function DagTotaal({ totaal, norm, fout }) {
    const kleur = dagKleur(totaal, norm);
    const over = rond(totaal - norm);
    const label = `${rond(totaal)}h / ${norm}h${over > 0 ? ` (+${over})` : ''}`;
    if (fout) {
        // Na een geweigerde "Schrijf dag": blijft rood tot de dag weer binnen de regel valt.
        return (
            <Tooltip arrow title={`Te veel interne uren: ${komma(fout.totaal)} u, waarvan ${komma(fout.intern)} u intern. `
                + `Boven ${DAGGRENS_INTERN} u mag alleen met uitsluitend externe uren — haal minstens ${komma(teVeelIntern(fout))} u intern weg.`}>
                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                    <Chip size="small" data-testid="dagtotaal" data-fout="intern" label={label}
                        sx={{ height: 20, fontSize: 10.5, fontWeight: 800, bgcolor: '#b91c1c', color: '#fff', border: '1px solid #b91c1c' }} />
                    <Typography component="span" sx={{ fontSize: 11, fontWeight: 700, color: '#b91c1c' }}>Te veel interne uren</Typography>
                </Box>
            </Tooltip>
        );
    }
    return (
        <Tooltip title="Geschreven + aangevinkt, tegen je dagnorm. Meer dan de norm kan alleen met uitsluitend externe uren." arrow>
            <Chip size="small" data-testid="dagtotaal" label={label}
                sx={{ height: 20, fontSize: 10.5, fontWeight: 800, bgcolor: '#fff', border: '1px solid', borderColor: kleur, color: kleur }} />
        </Tooltip>
    );
}

// ── Main component ─────────────────────────────────────────────────────────

export default function SlimUren() {
    const { instance } = useMsal();
    const activeAccount = instance.getActiveAccount();
    const userEmail = activeAccount?.username?.toLowerCase();

    const [weeksAgo, setWeeksAgo] = useState(0);
    const [loading, setLoading] = useState(false);
    const [writing, setWriting] = useState(false);
    const [error, setError] = useState(null);
    // Fout bij "Schrijf dag"/"Schrijf alles" als dialoog (de balk bovenaan zag je niet als je lager op de
    // pagina werkte, Joris okt 2026) + de dagen die op de daggrens zijn geweigerd, voor de markering in de dagkop.
    const [schrijfFout, setSchrijfFout] = useState(null);   // { titel, tekst?, dagen?: [{day, totaal, intern}] }
    const [grensDagen, setGrensDagen] = useState([]);
    const [snackbar, setSnackbar] = useState(null);

    const [data, setData] = useState(null);
    const [suggestions, setSuggestions] = useState([]);
    const [projectLines, setProjectLines] = useState({});
    const [recentProjects, setRecentProjects] = useState([]);
    const [calendarWarning, setCalendarWarning] = useState(null);

    const originalSuggestions = useRef([]);
    const calEventsRef = useRef({});   // agenda per dag, voor 'dag opnieuw voorstellen'
    // Spiegel van projectLines zodat handleProjectSelect stabiel kan blijven (memo-vriendelijk).
    const projectLinesRef = useRef(projectLines);
    projectLinesRef.current = projectLines;
    // Dubbele-write guard: state is te traag voor snelle dubbelkliks.
    const writingRef = useRef(false);

    const weekStart = getMonday(weeksAgo);
    useEffect(() => { setCalendarWarning(null); }, [weekStart]);   // melding hoort bij de week waarin hij ontstond
    // Actuele week voor asynchrone acties: een closure-waarde blijft gelijk na een weekwissel (Codex r1 #1).
    const weekRef = useRef(weekStart);
    weekRef.current = weekStart;
    const weekNum = getWeekNumber(weekStart);
    const weekDays = getWeekDays(weekStart);

    // Werkstaat per week ONTHOUDEN (ook vóór 'uren schrijven') — refresh of week-wissel
    // verliest niks meer. Opslag per gebruiker+week in localStorage.
    const storageKey = `slimuren-v2-${userEmail || 'onbekend'}-${weekStart}`;
    const [weekPreview, setWeekPreview] = useState(null); // al-geschreven uren (zonder genereren)

    useEffect(() => {
        setError(null);
        setCalendarWarning(null);
        setWeekPreview(null);
        try {
            const saved = JSON.parse(localStorage.getItem(storageKey) || 'null');
            if (saved?.data && Array.isArray(saved.suggestions) && saved.suggestions.length) {
                setData(saved.data);
                setSuggestions(saved.suggestions);
                setProjectLines(saved.projectLines || {});
                originalSuggestions.current = saved.original || [];
                return;
            }
        } catch { /* corrupte opslag — vers beginnen */ }
        setData(null);
        setSuggestions([]);
        originalSuggestions.current = [];
    }, [storageKey]);

    // Elke wijziging bewaren (fail-soft bij vol localStorage).
    useEffect(() => {
        if (!data || !suggestions.length) return;
        try {
            localStorage.setItem(storageKey, JSON.stringify({
                data, suggestions, projectLines, original: originalSuggestions.current, savedAt: Date.now(),
            }));
        } catch {
            try {   // quota: bewaar dan alleen het essentiële (suggesties + employee)
                localStorage.setItem(storageKey, JSON.stringify({
                    data: { employee: data.employee, weekStart: data.weekStart, existingHours: data.existingHours, existingEntries: data.existingEntries, tasksByDay: {}, projectLines: {}, internProject: data.internProject },
                    suggestions, projectLines: {}, original: [], savedAt: Date.now(),
                }));
            } catch { /* geen opslag mogelijk */ }
        }
    }, [data, suggestions, projectLines, storageKey]);

    // Zonder werkstaat: meteen de al-geschreven Gripp-uren van deze week tonen
    // (licht endpoint, geen GPT) — je hoeft niet eerst op Genereer te klikken.
    useEffect(() => {
        if (data || !userEmail) return;
        let active = true;
        authFetch(`${BACKEND_URL}/Api/slim-uren/existing?weekStart=${weekStart}`)
            .then(r => leesJson(r, 'Al geschreven uren laden'))
            .then(d => { if (active && !d.error) setWeekPreview(d); })
            .catch(e => { if (active) setError(e.message); });
        return () => { active = false; };
    }, [data, userEmail, weekStart]);

    // ── Fetch Outlook calendar events ──────────────────────────────────────

    const fetchCalendar = useCallback(async () => {
        // Items to always filter out
        const SKIP_PREFIXES = ['optie:', 'lunch'];
        const SKIP_CONTAINS = ['lunch', 'optie:'];

        const voorWeek = weekStart;   // late antwoorden van een vorige week mogen de melding niet zetten
        try {
            // Gedeelde helper: alleen bij InteractionRequired een popup (niet bij élke stille fout — die
            // popup blokkeerde de browser, tool-feedback #64); daarna alle pagina's van de week.
            const token = await acquireGraphToken(['Calendars.Read']);
            const days = getWeekDays(weekStart);
            const startDT = `${days[0]}T00:00:00`;
            const endDT = `${days[4]}T23:59:59`;
            const events = await haalAgendaOp(token, startDT, endDT);

            const byDay = {};
            for (const day of days) byDay[day] = [];

            for (const ev of events) {
                if (ev.isCancelled) continue;
                const subject = ev.subject || '';
                const subjectLower = subject.toLowerCase().trim();

                // Skip filtered items
                if (SKIP_PREFIXES.some(p => subjectLower.startsWith(p))) continue;
                if (SKIP_CONTAINS.some(c => subjectLower === c)) continue;

                const startDate = ev.start?.dateTime?.substring(0, 10);
                if (startDate && byDay[startDate]) {
                    const startTime = ev.start?.dateTime?.substring(11, 16) || '';
                    const endTime = ev.end?.dateTime?.substring(11, 16) || '';
                    const durationMs = new Date(ev.end.dateTime) - new Date(ev.start.dateTime);
                    const durationH = Math.round(durationMs / 3600000 * 4) / 4;
                    byDay[startDate].push({
                        id: `cal_${ev.id || Math.random()}`,
                        name: subject,
                        type: ev.isAllDay ? 'Hele dag' : 'Meeting',
                        hours: ev.isAllDay ? 8 : durationH,
                        startTime,
                        endTime,
                        source: 'outlook',
                        isAllDay: ev.isAllDay,
                        showAs: ev.showAs,
                        attendees: (ev.attendees || [])
                            .filter(a => a.type !== 'resource')
                            .map(a => a.emailAddress?.name || a.emailAddress?.address?.split('@')[0] || '')
                            .filter(Boolean)
                            .slice(0, 5),
                        // Domeinen van externe deelnemers: de engine herkent daarmee de klant ("Gereedschap" + @alliander → Alliander-project).
                        // Projectnummer in de eerste regels van de agendatekst (alleen het nummer gaat mee).
                        bodyNummers: nummersUitTekst(ev.bodyPreview), ...offerteUitTekst(ev.bodyPreview, subject),
                        attendeeDomains: [...new Set((ev.attendees || [])
                            .map(a => String(a.emailAddress?.address || '').split('@')[1] || '')
                            .filter(d => d && !/(^|\.)(fullframe\.nl|full-frame\.nl)$/i.test(d)))].slice(0, 8),
                    });
                }
            }
            if (weekRef.current === voorWeek) setCalendarWarning(null);
            return byDay;
        } catch (e) {
            console.warn('Agenda laden mislukt:', e);
            // Niet fataal — suggesties werken ook zonder agenda, maar zeg wát er misging.
            if (weekRef.current === voorWeek) setCalendarWarning(agendaFoutmelding(e));
            return {};
        }
    }, [weekStart]);

    // ── Fetch recent projects ──────────────────────────────────────────────

    useEffect(() => {
        if (!data?.employee?.grippId) return;
        authFetch(`${BACKEND_URL}/Api/slim-uren/recent-projects?employeeId=${data.employee.grippId}`)
            .then(r => leesJson(r, 'Recente projecten laden'))
            .then(d => setRecentProjects(d.projects || []))
            .catch(() => {}); // bewust stil: recente projecten zijn een hulpmiddel bij het kiezen — zoeken werkt zonder
    }, [data?.employee?.grippId]);

    // ── Generate ───────────────────────────────────────────────────────────

    const generate = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            // Fetch calendar first, then send to backend together
            const calEvents = await fetchCalendar();
            calEventsRef.current = calEvents || {};

            // Flatten calendar events for backend
            const calendarItems = [];
            for (const [day, events] of Object.entries(calEvents)) {
                for (const ev of events) {
                    if (ev.isAllDay || ev.showAs === 'free') continue;
                    calendarItems.push({ ...ev, date: day });
                }
            }

            const suggestRes = await authFetch(`${BACKEND_URL}/Api/slim-uren/suggest`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: userEmail, weekStart, calendarEvents: calendarItems }),
            });

            if (!suggestRes.ok) {
                const err = await suggestRes.json().catch(() => ({}));
                throw new Error(err.error || `Fout ${suggestRes.status}`);
            }
            const result = await suggestRes.json();

            // Outlook-afspraken bij de agenda-items van de server (die gekoppelde afspraken al één keer heeft).
            setData({ ...result, tasksByDay: voegAgendaSamen(result.tasksByDay, calEvents) });
            setSuggestions(result.suggestions || []);
            setProjectLines(result.projectLines || {});
            originalSuggestions.current = JSON.parse(JSON.stringify(result.suggestions || []));
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [userEmail, weekStart, fetchCalendar]);

    // ── Suggestion CRUD ────────────────────────────────────────────────────

    // Eén dag opnieuw laten voorstellen, met een aanwijzing ("Nico de Bont is een klant").
    // Alleen de regels van die dag worden vervangen; de rest van de week blijft zoals je hem had.
    const [herberekenDag, setHerberekenDag] = useState(null);   // { day, hint }
    const [regelsOpen, setRegelsOpen] = useState(false);         // "Wat Slim Uren van je leerde" (leerlus, okt 2026)
    // Dagen inklappen (Joris, 16 sep): al geschreven dagen beginnen dicht; klik op de kop wisselt.
    const [ingeklapt, setIngeklapt] = useState({});   // { [day]: true|false } — expliciete keuze; anders standaard
    // Rolvoorkeur voor de projectlijn-volgorde (functie uit de personenspiegel).
    useEffect(() => {
        if (!userEmail) return;
        authFetch(`${BACKEND_URL}/Api/planner-todo/persons`).then((r) => (r.ok ? r.json() : null)).then((d) => {
            const lijst = Array.isArray(d) ? d : (d?.persons || d?.people || []);
            const ik = lijst.find((p) => String(p.email || '').toLowerCase() === userEmail);
            const fn = ik ? (Array.isArray(ik.function) ? ik.function.join(', ') : (ik.function || ik.functie || '')) : '';
            zetRolPrior(fn);
        }).catch(() => {});
    }, [userEmail]);

    // 📧 Verzonden mail van een dag → urensuggesties (offerte verstuurd = 0,5 u op dat project,
    // klantmail = 0,25 u per gesprek). Naar de server gaan alleen onderwerp, ontvangerdomeinen,
    // ontvangersnamen, bijlage-ja/nee, het gesprek-id, en (11 okt) mail-id + verzendtijd + webLink zodat je
    // de bronmail per voorstel kunt openen — geen mailinhoud, geen volledige adressen.
    const [mailBezig, setMailBezig] = useState(null);   // day
    const analyseerMail = async (day) => {
        if (mailBezig) return;
        setMailBezig(day);
        try {
            const token = await acquireGraphToken(['Mail.Read']);
            const volgende = new Date(`${day}T00:00:00`); volgende.setDate(volgende.getDate() + 1);
            const van = new Date(`${day}T00:00:00`).toISOString(); const tot = volgende.toISOString();
            const url = `https://graph.microsoft.com/v1.0/me/mailFolders/sentitems/messages?$filter=sentDateTime ge ${van} and sentDateTime lt ${tot}&$select=id,subject,toRecipients,ccRecipients,hasAttachments,conversationId,sentDateTime,webLink,bodyPreview&$top=100&$orderby=sentDateTime desc`;
            const weekBijStart = weekStart;
            let berichten = []; let next = url;
            for (let pagina = 0; next && pagina < 5; pagina += 1) {          // max 500 mails per dag
                // ImmutableId: het id blijft geldig als de mail later van map wisselt (popover "bronmail").
                const r = await fetch(next, { headers: { Authorization: `Bearer ${token}`, Prefer: 'IdType="ImmutableId"' } });   // eslint-disable-line no-await-in-loop
                if (!r.ok) throw new Error(`Outlook ${r.status}`);
                const j = await r.json();                                    // eslint-disable-line no-await-in-loop
                berichten = berichten.concat(j.value || []); next = j['@odata.nextLink'] || null;
            }
            // Automatische antwoorden op uitnodigingen ("Geaccepteerd: …") zijn geen werk (wiki-feedback 18 sep).
            const GEEN_WERK = /^\s*(geaccepteerd|accepted|voorlopig geaccepteerd|tentative|geweigerd|declined|afgewezen|automatisch antwoord|automatic reply|uitnodiging|invitation|geannuleerd|cancell?ed|bijgewerkt)\s*:?/i;
            const mails = berichten.filter((m) => !GEEN_WERK.test(m.subject || '')).map((m) => {
                const adressen = [...(m.toRecipients || []), ...(m.ccRecipients || [])].map((x) => x.emailAddress || {});
                return {
                    id: m.id || '', sentDateTime: m.sentDateTime || '', bodyNummers: nummersUitTekst(m.bodyPreview), ...offerteUitTekst(m.bodyPreview, m.subject),
                    subject: m.subject || '', hasAttachment: Boolean(m.hasAttachments), conversationId: m.conversationId || '', webLink: m.webLink || '',
                    toDomains: [...new Set(adressen.map((a) => String(a.address || '').split('@')[1] || '').filter(Boolean))],
                    toNames: adressen.map((a) => a.name || '').filter(Boolean).slice(0, 5),
                };
            });
            if (!mails.length) { toast(`${formatDayShort(day)}: geen verzonden mail gevonden`); return; }
            const res = await authFetch(`${BACKEND_URL}/Api/slim-uren/mail-suggesties`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ date: day, mails }),
            });
            const d = await leesJson(res, 'Mail analyseren');
            const nieuw = [];
            for (const sug of (d.suggestions || [])) {
                const pn = String(sug.projectNumber);
                const intern = pn === 'Intern';   // over een offerte, maar geen duidelijke offerte: de oude regel (intern)
                let lines = intern ? (projectLinesRef.current.Intern || data?.projectLines?.Intern || []) : (projectLinesRef.current[pn] || []);
                if (!lines.length && !intern) {
                    try {
                        const lr = await authFetch(regelsUrl(pn));   // eslint-disable-line no-await-in-loop
                        lines = (await leesJson(lr, 'Offerteregels laden')).lines || [];                    // eslint-disable-line no-await-in-loop
                        setProjectLines((prev) => ({ ...prev, [pn]: lines }));
                    } catch { lines = []; }
                }
                const naam = (l) => l.name || l.additionalsubject || '';
                const trefwoord = String(sug.regelTrefwoord || '').toLowerCase();
                const lijn = intern
                    ? (lines.find((l) => trefwoord && naam(l).toLowerCase().includes(trefwoord)) || null)
                    : isOfferteSleutel(pn) ? offerteRegel(lines)   // offerte-uren: Projectbegeleiding of het dichtstbij, anders zelf kiezen
                        : (lines.find((l) => /projectbegeleiding/i.test(naam(l))) || lines.find((l) => /communicatie/i.test(naam(l)))
                            || opVoorkeur(lines.filter((l) => !/groep/i.test(naam(l))), naam)[0] || null);   // geen begeleidingsregel: je rolvoorkeur, niet blind de eerste
                // Niet dubbel (Codex #7): al een mail-suggestie voor dit project op deze dag, of al
                // geschreven uren in Gripp op dezelfde projectlijn die dag → overslaan.
                // per soort (offerte vs. mailcontact), zodat een later gevonden offerte niet wegvalt achter een eerdere mail-suggestie
                const soortVan = (d) => (/^Offerte/i.test(d || '') ? 'offerte' : 'mail');
                const alSuggestie = suggestions.some((x) => String(x.date || '').slice(0, 10) === day && String(x.projectNumber) === pn && x.source === 'mail' && soortVan(x.description) === soortVan(sug.description));
                const alGeschreven = lijn && (data?.existingEntries || []).some((e) => String(e.date || '').slice(0, 10) === day && Number(e.projectLineId) === Number(lijn.id));
                if (alSuggestie || alGeschreven) continue;
                nieuw.push({
                    id: `sug_mail_${day}_${Date.now()}_${nieuw.length}`, date: day, projectNumber: pn,
                    projectName: intern ? 'Intern' : (sug.projectName || `Project ${pn}`),
                    ...(sug.doel && sug.reden ? { geleerd: sug.reden } : {}),   // offerte of terugval intern: waarom
                    projectLineId: lijn?.id || null, projectLineName: lijn ? naam(lijn) : '', hours: sug.hours, description: sug.description,
                    source: 'mail', approved: true, isNew: true, confidence: 0.7,
                    links: Array.isArray(sug.links) ? sug.links : [],   // bronmails: ✉-chip per mail (11 okt)
                });
            }
            if (weekBijStart !== weekRef.current) { toast('Week gewisseld tijdens analyseren — resultaat niet toegevoegd'); return; }
            setSuggestions((prev) => [...prev, ...nieuw]);
            const extra = d.onbekend ? ` · ${d.onbekend} mail(s) zonder herkenbare klant` : '';
            toast[nieuw.length ? 'success' : 'error'](`${formatDayShort(day)}: ${d.mails} verzonden mails → ${nieuw.length} suggesties${extra}`, { duration: 6000 });
        } catch (e) {
            meldFout(e, 'Mail analyseren');
        } finally {
            setMailBezig(null);
        }
    };
    const [herberekenBezig, setHerberekenBezig] = useState(false);
    const herberekenDagNu = async () => {
        const { day, hint } = herberekenDag || {};
        if (!day) return;
        setHerberekenBezig(true);
        try {
            const calendarItems = (calEventsRef.current?.[day] || [])
                .filter(ev => !ev.isAllDay && ev.showAs !== 'free')
                .map(ev => ({ ...ev, date: day }));
            const res = await authFetch(`${BACKEND_URL}/Api/slim-uren/suggest`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: userEmail, weekStart, calendarEvents: calendarItems, dates: [day], hint: hint || '' }),
            });
            const result = await leesJson(res, 'Opnieuw voorstellen');
            const stamp = Date.now();
            const nieuw = (result.suggestions || [])
                .filter(x => String(x.date || '').slice(0, 10) === day)
                .map((x, i) => ({ ...x, id: `sug_${day}_${stamp}_${i}`, approved: true }));
            setSuggestions(prev => [...prev.filter(x => String(x.date || '').slice(0, 10) !== day || x.written), ...nieuw]);
            // Ook deze voorstellen als uitgangspunt onthouden, anders telt een wijziging niet als correctie.
            originalSuggestions.current = [...originalSuggestions.current, ...JSON.parse(JSON.stringify(nieuw))];
            setProjectLines(prev => ({ ...prev, ...(result.projectLines || {}) }));
            setData(prev => (prev ? { ...prev, projectLines: { ...(prev.projectLines || {}), ...(result.projectLines || {}) } } : prev));
            toast.success(`${formatDayShort(day)}: ${nieuw.length} nieuwe voorstellen`);
            setHerberekenDag(null);
        } catch (e) {
            meldFout(e, 'Opnieuw voorstellen');
        } finally {
            setHerberekenBezig(false);
        }
    };

    // Stabiele (id-gebaseerde) callbacks zodat gememoizede rijen niet onnodig re-renderen.
    const updateSuggestion = useCallback((id, field, value) => {
        setSuggestions(prev => prev.map(s => s.id === id ? { ...s, [field]: value } : s));
    }, []);
    const toggleApproval = useCallback((id) => {
        setSuggestions(prev => prev.map(s => s.id === id ? { ...s, approved: !s.approved } : s));
    }, []);
    const removeSuggestion = useCallback((id) => {
        setSuggestions(prev => prev.filter(s => s.id !== id));
    }, []);
    const addSuggestion = useCallback((day) => {
        setSuggestions(prev => [...prev, {
            id: `sug_new_${Date.now()}`,
            date: day, projectNumber: '', projectName: '', projectLineId: null,
            projectLineName: '', hours: 1, description: '', source: 'manual',
            confidence: 1.0, approved: true, isNew: true,
        }]);
    }, []);

    const handleProjectSelect = useCallback(async (sugId, project, preferredLine = null) => {
        if (!project) return;
        const pn = String(project.projectNumber || project.projectId);
        const lookupId = String(project.lookupProjectId || project.projectNumber || project.projectId);
        const cached = projectLinesRef.current;
        let lines = cached[pn] || cached[lookupId] || [];
        if (lines.length === 0 && lookupId) {
            try {
                const res = await authFetch(regelsUrl(lookupId));
                const d = await leesJson(res, 'Offerteregels laden');
                lines = d.lines || [];
                setProjectLines(prev => ({ ...prev, [pn]: lines, [lookupId]: lines }));
            } catch (e) { setError(e.message); }
        }
        setSuggestions(prev => prev.map(s => {
            if (s.id !== sugId) return s;
            // Offerte: Projectbegeleiding en communicatie (of het dichtstbij), nooit blind de eerste regel.
            const selectedLine = preferredLine || (isOfferteSleutel(pn) ? offerteRegel(lines) : lines[0]) || null;
            return { ...s, projectNumber: pn, projectName: project.projectName || `Project ${pn}`,
                projectLineId: selectedLine?.id || null, projectLineName: selectedLine?.name || '' };
        }));
    }, []);

    // ── Write to Gripp ─────────────────────────────────────────────────────

    // REGEL (pre-check; de server valideert ook): max 8u per dag zodra er die dag
    // interne uren bij zitten — alleen puur externe uren mogen boven de 8u.
    // Cursus/training/opleiding is intern geboekt maar telt voor deze regel als extern:
    // een cursusdag van 6u + wat overleg mag gewoon (Joris, 15 sep).
    const internLineIds = useMemo(() => {
        const teltAlsExtern = (l) => /cursus|training|opleiding/i.test(String(l?.name || l?.searchname || ''));
        return new Set((data?.projectLines?.Intern || []).filter(l => !teltAlsExtern(l)).map(l => l.id));
    }, [data?.projectLines]);
    // Voorstel (heeft `approved`): interne regel of het label Intern; al geschreven Gripp-uur: alleen de regel.
    const isIntern = useCallback((x) => internLineIds.has(Number(x.projectLineId))
        || ('approved' in x && x.projectNumber === 'Intern'), [internLineIds]);

    // Live: welke dagen nu boven de grens zitten (aangevinkt + geschreven), voor de rode markering in de dagkop.
    // De markering verschijnt pas na een geweigerde schrijfpoging en verdwijnt zodra de dag weer klopt.
    // In deze sessie geschreven en nog niet in de geladen Gripp-uren: telt als bestaand bij de volgende schrijfactie.
    const sessieUren = useMemo(() => sessieGeschrevenRegels(suggestions, data?.existingEntries), [suggestions, data?.existingEntries]);
    // Daggrens volgens de server (kent alle Gripp-uren), per dag met een vingerafdruk van die dag: blijft staan
    // tot er op díe dag iets verandert.
    const [serverGrens, setServerGrens] = useState({});   // { [day]: { o, vinger } }
    const vingerafdruk = useCallback((day) => JSON.stringify(suggestions
        .filter((x) => String(x.date || '').slice(0, 10) === day)
        .map((x) => [x.id, x.approved, Number(x.hours) || 0, x.projectLineId, Boolean(x.written)])), [suggestions]);
    const grensNu = useMemo(() => {
        const uit = {};
        const bestaand = [...(data?.existingEntries || []), ...sessieUren];
        const open = suggestions.filter((x) => x.approved && !x.written && Number(x.hours) > 0);
        const dubbel = alGeboekt(open, bestaand);   // al geladen óf in deze sessie geschreven
        for (const o of dagGrensOvertredingen(open.filter((x) => !dubbel.has(x.id)), bestaand, isIntern)) uit[o.day] = o;
        for (const [d, v] of Object.entries(serverGrens)) if (!uit[d] && v.vinger === vingerafdruk(d)) uit[d] = v.o;
        return uit;
    }, [suggestions, data?.existingEntries, sessieUren, isIntern, serverGrens, vingerafdruk]);
    useEffect(() => {
        setGrensDagen((prev) => (prev.every((d) => grensNu[d]) ? prev : prev.filter((d) => grensNu[d])));
    }, [grensNu]);

    const naarDag = useCallback((day) => {
        setSchrijfFout(null);
        setIngeklapt((prev) => ({ ...prev, [day]: false }));
        setTimeout(() => {
            const kop = dagRefs.current[day]?.kop;
            if (kop?.scrollIntoView) kop.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 0);
    }, []);

    // Write a specific set of suggestions to Gripp
    const doWrite = async (toWrite, label, overgeslagen = 0) => {
        if (toWrite.length === 0) {
            setSchrijfFout({ titel: 'Niets om te schrijven', tekst: overgeslagen
                ? `${overgeslagen} regel${overgeslagen > 1 ? 's hebben' : ' heeft'} nog geen Gripp-projectlijn. Klik op het potloodje om een project te kiezen.`
                : 'Geen aangevinkte regels met project en uren.' });
            return;
        }

        // Regels die al in de geladen Gripp-uren staan tellen niet dubbel: die slaat de server straks over.
        const bestaand = [...(data?.existingEntries || []), ...sessieUren];
        const dubbel = alGeboekt(toWrite, bestaand);
        const overIntern = dagGrensOvertredingen(toWrite.filter((x) => !dubbel.has(x.id)), bestaand, isIntern);
        if (overIntern.length) {
            setGrensDagen((prev) => [...new Set([...prev, ...overIntern.map((o) => o.day)])]);
            setSchrijfFout({ titel: overIntern.length === 1 ? `${formatDayShort(overIntern[0].day)} is niet geschreven` : 'Niet geschreven', dagen: overIntern });
            return;
        }

        // Wegschrijven is een externe, niet-terug-te-draaien boeking in Gripp: eerst bevestigen.
        const totaalUren = Math.round(toWrite.reduce((sum, s) => sum + (Number(s.hours) || 0), 0) * 100) / 100;
        const aantalDagen = new Set(toWrite.map(s => (s.date || '').slice(0, 10))).size;
        if (!(await bevestig({
            titel: 'Uren naar Gripp schrijven?',
            tekst: `${totaalUren} uur in ${toWrite.length} regel${toWrite.length === 1 ? '' : 's'} over ${aantalDagen} dag${aantalDagen === 1 ? '' : 'en'}${label ? ` (${label})` : ''} wordt definitief in Gripp geboekt.`
                + (overgeslagen ? ` ${overgeslagen} regel${overgeslagen > 1 ? 's' : ''} zonder Gripp-projectlijn ${overgeslagen > 1 ? 'worden' : 'wordt'} overgeslagen.` : ''),
            knop: 'Wegschrijven',
        }))) return;

        if (writingRef.current) return; // dubbelklik/parallelle write voorkomen
        writingRef.current = true;

        setWriting(true);
        setError(null);

        const feedbackEntries = toWrite.map(s => {
            const orig = originalSuggestions.current.find(o => o.id === s.id);
            let action = 'approved';
            // "+ uren" bij een afspraak zonder voorstel = gemist: daar had de AI iets moeten voorstellen (11 okt).
            if (s.gemist) action = 'gemist';
            else if (s.isNew) action = 'added';
            else if (orig && (orig.hours !== s.hours || orig.projectLineId !== s.projectLineId)) action = 'modified';
            return {
                suggested_project_line_id: orig?.projectLineId || null, suggested_hours: orig?.hours || null,
                actual_project_line_id: s.projectLineId, actual_hours: s.hours,
                task_type: s.source || '', task_name: s.description || '', project_number: s.projectNumber || '', action,
                // Voor het leren per persoon (wiki-feedback 26 sep): waar het voorstel vandaan kwam
                // en wat er eerst stond, zodat een gewijzigd project een correctie wordt.
                suggested_project_number: orig?.projectNumber || null,
                bron: orig?.bron || (s.gemist ? s.bron : null) || null,
                bron_titel: orig?.bronTitel || (s.gemist ? s.bronTitel : null) || null,
                deelnemer_domeinen: orig?.bronDomeinen || (s.gemist ? s.bronDomeinen : null) || [],
                project_name: s.projectName || '', project_line_name: s.projectLineName || '',
            };
        });

        try {
            const res = await authFetch(`${BACKEND_URL}/Api/slim-uren/write`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    hourlogs: toWrite.map(s => ({
                        projectLineId: s.projectLineId, hours: s.hours, date: s.date,
                        description: s.description || '', employeeId: data.employee.grippId,
                    })),
                    employeeId: data.employee.grippId,
                    feedbackData: { email: userEmail, week_start: weekStart, entries: feedbackEntries },
                }),
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                if (Array.isArray(err.dagen) && err.dagen.length) {
                    // De server zag de daggrens (ook met uren die de browser niet kent): zelfde dialoog + markering.
                    const dagen = err.dagen.filter((o) => o && o.day).map((o) => ({ day: String(o.day).slice(0, 10), totaal: rond(o.totaal), intern: rond(o.intern) }));
                    setGrensDagen((prev) => [...new Set([...prev, ...dagen.map((o) => o.day)])]);
                    setServerGrens((prev) => ({ ...prev, ...Object.fromEntries(dagen.map((o) => [o.day, { o, vinger: vingerafdruk(o.day) }])) }));
                    setSchrijfFout({ titel: dagen.length === 1 ? `${formatDayShort(dagen[0].day)} is niet geschreven` : 'Niet geschreven', dagen });
                    return;
                }
                throw new Error(err.error || `Fout ${res.status}`);
            }
            const result = await res.json();
            setSnackbar(`${result.written} uren geschreven naar Gripp${label ? ` (${label})` : ''}!`);
            if (result.feedbackOpgeslagen === false) {
                // De uren staan in Gripp; alleen het leren mist deze keer. Niet meer stil (okt 2026).
                toast.error('Uren geschreven, maar je correcties zijn niet opgeslagen om van te leren.');
            }
            // Alleen echt geschreven (of al in Gripp aanwezige) regels als geschreven markeren — een mislukte regel
            // blijft open, anders verdwijnt hij uit beeld zonder in Gripp te staan (okt 2026).
            const writtenIds = geschrevenIds(toWrite, result);
            const overgeslagen = (result.overgeslagen || []).length;
            if (overgeslagen) toast(`${overgeslagen} regel${overgeslagen > 1 ? 's stonden' : ' stond'} al in Gripp en ${overgeslagen > 1 ? 'zijn' : 'is'} niet nog eens geschreven.`, { duration: 6000 });
            if (result.failed > 0) setSchrijfFout({ titel: 'Niet alles is geschreven', tekst: `${result.failed} regel${result.failed > 1 ? 's' : ''} kon${result.failed > 1 ? 'den' : ''} niet naar Gripp: ${(result.errors || []).map((e) => e.error).filter(Boolean).slice(0, 2).join(' · ') || 'onbekende fout'}. Die regels staan nog open.` });
            // hourlogId bewaren: elke Gripp-boeking telt in de totalen één keer (sessieGeschrevenRegels), ook als een
            // regel al in Gripp stond (overgeslagen) of hetzelfde voorstel twee keer werd geschreven (Codex okt 2026).
            const hourlogPerIndex = new Map([...(result.results || []), ...(result.overgeslagen || [])]
                .filter((r) => Number.isInteger(r?.index)).map((r) => [r.index, r.hourlogId]));
            const perId = new Map(toWrite.map((x, i) => [x.id, hourlogPerIndex.get(i)]));
            setSuggestions(prev => prev.map(s => (writtenIds.has(s.id)
                ? { ...s, written: true, approved: true, hourlogId: perId.get(s.id) ?? s.hourlogId }
                : s)));
        } catch (err) {
            setSchrijfFout({ titel: 'Schrijven mislukt', tekst: err.message || 'Onbekende fout' });
        } finally { writingRef.current = false; setWriting(false); }
    };

    const writeToGripp = () => {
        const allApproved = suggestions.filter(s => s.approved && s.hours > 0 && !s.written);
        const writeable = allApproved.filter(s => s.projectLineId);
        doWrite(writeable, null, allApproved.length - writeable.length);
    };

    const writeDayToGripp = (day) => {
        // Alleen déze dag (datum genormaliseerd — suggesties kunnen ISO-met-tijd hebben).
        const allApproved = suggestions.filter(s => (s.date || '').slice(0, 10) === day && s.approved && s.hours > 0 && !s.written);
        const writeable = allApproved.filter(s => s.projectLineId);
        doWrite(writeable, formatDayShort(day), allApproved.length - writeable.length);
    };

    // ── Computed ───────────────────────────────────────────────────────────

    const approvedSuggestions = suggestions.filter(s => s.approved);
    // Open + in deze sessie geschreven (uniek per Gripp-boeking); wat al in de geladen uren zat telt via existingHours.
    const totalApproved = rond([...approvedSuggestions.filter(s => !s.written), ...sessieGeschrevenRegels(suggestions, data?.existingEntries)]
        .reduce((sum, s) => sum + (Number(s.hours) || 0), 0));
    // Open = aangevinkt, uren > 0 en nog niet geschreven: alleen die tellen in "Schrijf N regels" en gaan mee.
    const openRegels = approvedSuggestions.filter(s => !s.written && (Number(s.hours) || 0) > 0);
    const contractHours = data?.employee?.contractHours || 40;
    // Rooster (Standaard Werkdagen) + vrije dagen: die dagen tellen niet mee en krijgen geen dagnorm.
    const rooster = data?.rooster || {};
    const geenWerk = new Set([...(rooster.nietWerkdagen || []), ...(rooster.vrijeDagen || [])]);
    const dagnorm = Number(data?.employee?.dagdoel) || contractHours / 5;
    const existingHours = data?.existingHours || 0;
    const hoursRemaining = rond(contractHours - existingHours - totalApproved);
    const projectOptions = useMemo(() => {
        const internalProjectOption = data?.internProject ? {
            label: 'Intern - Full Frame',
            sourceGroup: 'Snel kiezen',
            ...data.internProject,
        } : null;
        const recentProjectOptions = recentProjects.map(p => {
            const status = (p.statusLabel || '').toLowerCase();
            return {
                label: p.companyName ? `${p.projectName || `Project ${p.projectNumber}`} - ${p.companyName}` : (p.projectName || `Project ${p.projectNumber}`),
                sourceGroup: 'Recent gebruikt',
                ...p,
                archived: !!p.archived || status === 'archief' || status === 'gearchiveerd',
            };
        });
        return internalProjectOption
            ? [internalProjectOption, ...recentProjectOptions.filter(p => String(p.projectId) !== String(internalProjectOption.projectId))]
            : recentProjectOptions;
    }, [data?.internProject, recentProjects]);
    const tasksByDay = data?.tasksByDay || {};

    // "+ uren" (11 okt): regel aan een afspraak zonder voorstel hangen.
    const [plusBezig, setPlusBezig] = useState(null);   // task-id
    const urenVoorItem = async (day, task) => {
        if (plusBezig || !task) return;
        setPlusBezig(task.id);
        const weekBijStart = weekStart;
        try {
            const gok = gokProject(task, data?.agendaHints?.[task.id], projectOptions, data?.internProject);
            let project = { projectNumber: '', projectName: '', projectLineId: null, projectLineName: '' };
            if (gok) {
                let lines = [];
                if (gok.intern) {
                    lines = projectLinesRef.current.Intern || data?.projectLines?.Intern || [];
                } else {
                    const pn = gok.projectNumber;
                    const lookup = String(gok.lookupId || pn);
                    lines = projectLinesRef.current[pn] || projectLinesRef.current[lookup] || [];
                    if (!lines.length) {
                        try {
                            const r = await authFetch(regelsUrl(lookup));
                            lines = (await leesJson(r, 'Offerteregels laden')).lines || [];
                            setProjectLines((prev) => ({ ...prev, [pn]: lines }));
                        } catch { lines = []; }   // geen regels: dan kies je hem zelf (potloodje staat open)
                    }
                }
                const regel = kiesRegel(lines, gok);
                project = { projectNumber: gok.projectNumber, projectName: gok.projectName,
                    projectLineId: regel?.id ?? null, projectLineName: regelNaam(regel) };
            }
            if (weekBijStart !== weekRef.current) return;   // week gewisseld tijdens het laden: niet in de andere week zetten
            const uren = Math.min(24, Math.max(0.25, Math.round((Number(task.hours) || 1) * 4) / 4));
            setSuggestions((prev) => [...prev, {
                id: `sug_plus_${task.id}_${Date.now()}`, date: day, taskId: task.id, ...project,
                hours: uren, description: task.name || '', source: 'manual', confidence: 1.0,
                approved: true, isNew: true, gemist: true,
                bron: task.source === 'outlook' ? 'agenda' : 'taak', bronTitel: task.name || '',
                bronDomeinen: task.attendeeDomains || [], agendaUren: Number(task.hours) || null,
                ...(gok?.reden ? { geleerd: `Project gekozen via ${gok.reden}` } : {}),
            }]);
        } finally {
            setPlusBezig(null);
        }
    };

    // Dagkop blijft in beeld (11 okt: "fijn als je de totalen blijft zien van de dag waarop je invult").
    // position: sticky werkt hier niet: de <main> van AppShell heeft overflow:auto en scrolt zelf niet, dus
    // plakt een sticky kop nergens aan (nagemeten in Chrome). Daarom een zwevende kopie van de dagkop zodra
    // de echte kop onder de appbalk verdwijnt terwijl je nog in die dag zit.
    const dagRefs = useRef({});
    const [zwevend, setZwevend] = useState(null);   // { day, left, width, top }
    useEffect(() => {
        if (!data) { setZwevend(null); return undefined; }
        const plan = window.requestAnimationFrame ? (cb) => window.requestAnimationFrame(cb) : (cb) => setTimeout(cb, 16);
        let gepland = false;
        const meet = () => {
            gepland = false;
            // Onderkant van de appbalk (incl. TEST-balk erboven): gemeten, niet aangenomen — werkt op test én productie.
            const balk = document.querySelector('header.MuiAppBar-root');
            const lijn = balk ? Math.max(0, Math.round(balk.getBoundingClientRect().bottom)) : 0;
            let gevonden = null;
            for (const [day, el] of Object.entries(dagRefs.current)) {
                if (!el?.paper || !el?.kop) continue;
                const p = el.paper.getBoundingClientRect();
                const k = el.kop.getBoundingClientRect();
                const hoogte = el.kop.offsetHeight || (k.bottom - k.top) || 48;
                // Zolang de dag doorloopt staat hij op de appbalk; aan het eind van de dag schuift hij mee omhoog.
                if (k.top < lijn && p.top < lijn && p.bottom > lijn) {
                    gevonden = { day, left: p.left, width: p.width, top: Math.round(Math.min(lijn, p.bottom - hoogte)) };
                    break;
                }
            }
            setZwevend((prev) => (prev?.day === gevonden?.day && prev?.left === gevonden?.left && prev?.width === gevonden?.width && prev?.top === gevonden?.top ? prev : gevonden));
        };
        const opScroll = () => { if (!gepland) { gepland = true; plan(meet); } };
        window.addEventListener('scroll', opScroll, true);   // capture: ook als een tussenliggende container scrolt
        window.addEventListener('resize', opScroll);
        opScroll();
        return () => { window.removeEventListener('scroll', opScroll, true); window.removeEventListener('resize', opScroll); };
    }, [data, ingeklapt, suggestions.length]);

    /** Totalen van één dag — dezelfde cijfers in de dagkop en in de zwevende kop. */
    const samenvattingVan = (day) => dagSamenvatting(suggestions.filter((x) => x.date === day), data?.existingEntries || [], day,
        geenWerk.has(day));

    const dagOk = (z) => z.vrijeDag || z.totaal >= dagnorm * 0.9;
    const dagKopStijl = (z) => ({
        px: 2, py: 1.5,
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 0.5,
        bgcolor: dagOk(z) ? 'success.light' : (z.dayTotal > 0 ? 'warning.light' : '#f8f9fa'),
    });

    /** Inhoud van de dagkop — dezelfde in de echte kop en in de zwevende kop (render-functie, geen component). */
    const dagKopInhoud = (day, z, dicht = false) => {
        const vrijeDagLabel = (rooster.vrijeDagen || []).includes(day) ? 'Vrije dag' : 'Geen werkdag (rooster)';
        const grensFout = grensDagen.includes(day) ? grensNu[day] : null;
        return (<>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, textTransform: 'uppercase', fontSize: 13, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <span style={{ display: 'inline-block', transition: 'transform 0.15s', transform: dicht ? 'rotate(-90deg)' : 'none', fontSize: 11 }}>▼</span>
                {formatDayShort(day)}
                {dicht && z.dayExisting > 0 && <Typography component="span" variant="caption" sx={{ ml: 1, color: 'text.secondary', textTransform: 'none' }}>ingeklapt · {z.dayExisting}h geschreven</Typography>}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }} onClick={(e) => e.stopPropagation()}>
                {(z.dayExisting + z.sessieGeschreven) > 0 && (
                    <Chip label={`${rond(z.dayExisting + z.sessieGeschreven)}h geschreven`} size="small"
                        sx={{ height: 20, fontSize: 10, fontWeight: 600, bgcolor: 'success.light', color: 'success.main' }} />
                )}
                {z.dayTotal > 0 && <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>+{z.dayTotal}h nieuw</Typography>}
                {z.vrijeDag && (
                    <Tooltip title={rooster.roosterDagen ? `Rooster: ${rooster.roosterDagen.join(' ')} · dagnorm ${dagnorm}u` : ''} arrow>
                        <Chip size="small" label={vrijeDagLabel} sx={{ height: 20, fontSize: 10.5, fontWeight: 700, bgcolor: '#eef2ff', color: '#3730a3' }} />
                    </Tooltip>
                )}
                {!z.vrijeDag && <DagTotaal totaal={z.totaal} norm={dagnorm} fout={grensFout} />}
                {dagOk(z)
                    ? <CheckCircleIcon sx={{ fontSize: 18, color: 'success.main' }} />
                    : z.totaal > 0 && <WarningAmberIcon sx={{ fontSize: 18, color: 'warning.main' }} />}
                <Tooltip title="Verzonden mail van deze dag analyseren → uren op offertes en klantprojecten" arrow>
                    <span>
                        <IconButton size="small" onClick={() => analyseerMail(day)} disabled={loading || writing || Boolean(mailBezig)}
                            aria-label="Mail analyseren" sx={{ p: 0.4 }}>
                            {mailBezig === day ? <CircularProgress size={14} /> : <span style={{ fontSize: 15, lineHeight: 1 }}>📧</span>}
                        </IconButton>
                    </span>
                </Tooltip>
                <Tooltip title="Deze dag opnieuw laten voorstellen, eventueel met een aanwijzing" arrow>
                    <span>
                        <IconButton size="small" onClick={() => setHerberekenDag({ day, hint: '' })} disabled={loading || writing}
                            aria-label="Dag opnieuw voorstellen" sx={{ p: 0.4 }}>
                            <RefreshIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                    </span>
                </Tooltip>
                {z.allesGeschreven
                    ? <Chip label="Geschreven" size="small" sx={{ height: 22, fontSize: 11, fontWeight: 600, bgcolor: 'success.light', color: 'success.main' }} />
                    : z.schrijfbaar > 0 && (
                        <Button size="small" variant="outlined"
                            startIcon={writing ? <CircularProgress size={12} /> : <SendIcon sx={{ fontSize: 14 }} />}
                            onClick={() => writeDayToGripp(day)}
                            disabled={writing}
                            sx={{ ml: 1, fontSize: 11, py: 0, px: 1, minHeight: 26, textTransform: 'none' }}>
                            Schrijf dag
                        </Button>
                    )}
            </Box>
        </>);
    };

    // ── Render ─────────────────────────────────────────────────────────────

    return (
        <Box sx={{ width: '100%', maxWidth: 1800, mx: 'auto', px: { xs: 2, sm: 3, md: 5 }, pt: { xs: 3, md: 5 }, pb: 8 }}>
            <PageHeader
                icon="timer"
                title="Slim Uren"
                subtitle="AI-gestuurde suggesties op basis van je taken en agenda"
                rightActions={(
                    <Paper variant="outlined" sx={{ px: 1, py: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <IconButton onClick={() => setWeeksAgo(w => w + 1)} size="small" aria-label="Vorige week">
                            <NavigateBeforeIcon />

            <Dialog open={Boolean(herberekenDag)} onClose={() => !herberekenBezig && setHerberekenDag(null)} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ fontWeight: 700 }}>
                    {herberekenDag ? `${formatDayShort(herberekenDag.day)} opnieuw voorstellen` : ''}
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.5 }}>
                        De regels van deze dag worden opnieuw voorgesteld; al geschreven regels blijven staan.
                        Geef eventueel een aanwijzing mee.
                    </Typography>
                    <TextField autoFocus fullWidth multiline minRows={2} size="small"
                        label="Aanwijzing (optioneel)"
                        placeholder="Bijv. Nico de Bont is een klant — boek de kick-off op dat project"
                        value={herberekenDag?.hint || ''}
                        onChange={(e) => setHerberekenDag(prev => ({ ...prev, hint: e.target.value }))} />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setHerberekenDag(null)} disabled={herberekenBezig} sx={{ textTransform: 'none' }}>Annuleren</Button>
                    <Button variant="contained" disableElevation onClick={herberekenDagNu} disabled={herberekenBezig}
                        startIcon={herberekenBezig ? <CircularProgress size={14} /> : <RefreshIcon />} sx={{ textTransform: 'none' }}>
                        Opnieuw voorstellen
                    </Button>
                </DialogActions>
            </Dialog>
                        </IconButton>
                        <Box sx={{ textAlign: 'center', minWidth: 150 }}>
                            <Typography sx={{ fontWeight: 700, fontSize: 14, lineHeight: 1.2 }}>Week {weekNum}</Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.1, display: 'block' }}>
                                {formatDay(weekDays[0])} — {formatDay(weekDays[4])}
                            </Typography>
                        </Box>
                        <IconButton onClick={() => setWeeksAgo(w => Math.max(0, w - 1))} disabled={weeksAgo === 0} size="small" aria-label="Volgende week">
                            <NavigateNextIcon />
                        </IconButton>
                    </Paper>
                )}
            />

            {/* Leerlus (okt 2026): hoe goed was het voorstel, en wat heeft Slim Uren van je geleerd. */}
            <KwaliteitTegel />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: -1.5, mb: 1.5 }}>
                <Button size="small" startIcon={<HistoryIcon sx={{ fontSize: 16 }} />} onClick={() => setRegelsOpen(true)}
                    sx={{ textTransform: 'none', fontSize: 12, color: 'text.secondary' }}>
                    Wat Slim Uren van je leerde
                </Button>
            </Box>
            <Dialog open={regelsOpen} onClose={() => setRegelsOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle sx={{ fontWeight: 700 }}>Wat Slim Uren van je leerde</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.5 }}>
                        Elke nacht legt Slim Uren je voorstel naast wat je in Gripp schreef. Wat je steeds hetzelfde doet wordt
                        een regel die vóór de AI gaat. Zet een regel uit als hij niet klopt; uren aanpassen houdt hem vast.
                    </Typography>
                    {regelsOpen && <MijnRegels />}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRegelsOpen(false)} sx={{ textTransform: 'none' }}>Sluiten</Button>
                </DialogActions>
            </Dialog>

            {/* Startscherm: al-geschreven uren direct zichtbaar + genereer-knop */}
            {!data && (
                <Paper variant="outlined" sx={{ textAlign: 'center', py: 5, px: 3, maxWidth: 640, mx: 'auto' }}>
                    <Typography sx={{ fontWeight: 700, mb: 0.5 }}>Week {weekNum} invullen</Typography>

                    {/* Al geschreven in Gripp — zonder eerst te hoeven genereren */}
                    {weekPreview && weekPreview.total > 0 && (
                        <Box sx={{ textAlign: 'left', maxWidth: 460, mx: 'auto', my: 2, p: 1.5, borderRadius: 1.5, bgcolor: '#f0fdf4', border: '1px solid #bbf7d0' }}>
                            <Typography variant="caption" sx={{ fontWeight: 800, color: '#166534', display: 'block', mb: 0.5 }}>
                                ✔ Al geschreven deze week: {weekPreview.total}u
                            </Typography>
                            {Object.entries(weekPreview.byDay || {}).sort().map(([d, h]) => (
                                <Typography key={d} variant="caption" sx={{ display: 'block', color: '#166534' }}>
                                    {formatDayShort(d)}: {h}u
                                    {(weekPreview.entries || []).filter(e => e.date === d).slice(0, 4).map(e => ` · ${e.line || e.project || 'Gripp'}`).join('')}
                                </Typography>
                            ))}
                        </Box>
                    )}
                    {weekPreview && weekPreview.total === 0 && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                            Nog niets geschreven in Gripp voor deze week.
                        </Typography>
                    )}

                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        Slim Uren kijkt naar je taken, Outlook-agenda en eerdere weken, en zet
                        een voorstel klaar dat je per regel kunt aanpassen vóór het naar Gripp gaat.
                        Je werk wordt per week onthouden — ook zonder te schrijven.
                    </Typography>
                    <Button variant="contained" size="large"
                        startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <AutoFixHighIcon />}
                        onClick={generate} disabled={loading} sx={{ px: 4, py: 1.5 }}>
                        {loading ? 'Suggesties genereren...' : 'Genereer Suggesties'}
                    </Button>
                    {loading && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
                            Agenda ophalen en AI-voorstel maken — dit kan tot ±20 seconden duren.
                        </Typography>
                    )}
                </Paper>
            )}

            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
            {calendarWarning && (
                <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setCalendarWarning(null)}>{calendarWarning}</Alert>
            )}

            {/* Hoe dit voorstel tot stand kwam (okt 2026): een terugval zonder AI is zichtbaar, en wat als
                ruis is overgeslagen (privé, reistijd, dubbele blokken) staat erbij. */}
            {data && (data.bron === 'fallback' || (data.weggefilterd || []).length > 0) && (
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                    {data.bron === 'fallback' && (
                        <Tooltip title={`Het AI-voorstel lukte niet${data.bronFout ? ` (${data.bronFout})` : ''}; dit voorstel komt uit de vaste regels. Controleer de projecten extra goed of klik op Opnieuw.`}>
                            <Chip size="small" label="zonder AI" variant="outlined"
                                sx={{ height: 22, fontSize: 11, fontWeight: 700, color: '#92400e', borderColor: '#fcd34d' }} />
                        </Tooltip>
                    )}
                    {(data.weggefilterd || []).length > 0 && (
                        <Tooltip title={(data.weggefilterd || []).slice(0, 12).map((w) => `${w.name} — ${w.reden}`).join(' · ')}>
                            <Chip size="small" variant="outlined"
                                label={`${data.weggefilterd.length} agenda-item${data.weggefilterd.length === 1 ? '' : 's'} overgeslagen`}
                                sx={{ height: 22, fontSize: 11, color: 'text.secondary' }} />
                        </Tooltip>
                    )}
                </Box>
            )}

            {data && existingHours > 0 && (
                <Alert severity="info" sx={{ mb: 2 }}>
                    Er staan al {existingHours}h geschreven voor deze week in Gripp.
                </Alert>
            )}

            {/* Lege staat: generatie gelukt maar niets gevonden */}
            {data && suggestions.length === 0 && (
                <Alert severity="info" sx={{ mb: 2 }}>
                    Geen suggesties gevonden voor deze week — geen taken of agenda-items.
                    Voeg hieronder per dag handmatig regels toe met “Regel toevoegen”.
                </Alert>
            )}

            {/* Day-by-day: aligned rows */}
            {data && weekDays.map(day => {
                const daySuggestions = suggestions.filter(s => s.date === day);
                const dayTasksRaw = tasksByDay[day] || [];
                // Filter out non-work items
                const dayTasks = dayTasksRaw.filter(t => {
                    const prio = (t.priority || '').toLowerCase();
                    const type = (t.type || '').toLowerCase();
                    const name = (t.name || '').toLowerCase();
                    return !SKIP_TYPES.has(prio) && !SKIP_TYPES.has(type) && !SKIP_NAMES.has(name);
                });
                const z = samenvattingVan(day);
                const { dayExisting } = z;

                // Build paired rows: link suggestions to tasks, then add orphans
                const pairedRows = [];
                const usedSugIds = new Set();
                const usedTaskIds = new Set();

                // 1. Pair tasks with their linked suggestion (by taskId)
                for (const task of dayTasks) {
                    const linked = daySuggestions.find(s => s.taskId === task.id && !usedSugIds.has(s.id));
                    if (linked) {
                        pairedRows.push({ task, sug: linked });
                        usedSugIds.add(linked.id);
                        usedTaskIds.add(task.id);
                    }
                }
                // 2. Pair remaining tasks with unlinked suggestions (by description match)
                for (const task of dayTasks) {
                    if (usedTaskIds.has(task.id)) continue;
                    const byName = daySuggestions.find(s =>
                        !usedSugIds.has(s.id) && s.description && task.name &&
                        s.description.toLowerCase().includes(task.name.toLowerCase().substring(0, 10))
                    );
                    if (byName) {
                        pairedRows.push({ task, sug: byName });
                        usedSugIds.add(byName.id);
                    } else {
                        pairedRows.push({ task, sug: null });
                    }
                }
                // 3. Add orphan suggestions (auto rules, extras) at the end
                for (const sug of daySuggestions) {
                    if (!usedSugIds.has(sug.id)) {
                        pairedRows.push({ task: null, sug });
                    }
                }

                const dagGeschreven = daySuggestions.length
                    ? daySuggestions.every((x) => x.written || !x.approved)
                    : dayExisting > 0;
                const dicht = ingeklapt[day] ?? dagGeschreven;
                const wissel = () => setIngeklapt((prev) => ({ ...prev, [day]: !dicht }));
                return (
                    <Paper key={day} ref={(el) => { dagRefs.current[day] = { ...(dagRefs.current[day] || {}), paper: el }; }}
                        sx={{ mb: 2, overflow: 'hidden' }}>
                        {/* Day header — klikbaar om in/uit te klappen */}
                        <Box onClick={wissel} data-testid={`dagkop-${day}`}
                            ref={(el) => { dagRefs.current[day] = { ...(dagRefs.current[day] || {}), kop: el }; }} sx={{
                            cursor: 'pointer', scrollMarginTop: 80,
                            ...dagKopStijl(z),
                            borderBottom: '1px solid', borderColor: 'divider',
                        }}>
                            {dagKopInhoud(day, z, dicht)}
                        </Box>

                        {!dicht && (<>
                        {/* Column headers (agenda-kolom verdwijnt op mobiel — rijen stapelen daar) */}
                        <Box sx={{ display: { xs: 'none', md: 'flex' }, borderBottom: '1px solid', borderColor: 'divider', bgcolor: '#f8f9fa' }}>
                            <Box sx={{ flex: `0 0 ${AGENDA_KOLOM}`, px: 2, py: 0.75, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <EventIcon sx={{ fontSize: 13, color: 'text.disabled' }} />
                                <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5, fontSize: 10 }}>
                                    Agenda ({dayTasks.length})
                                </Typography>
                            </Box>
                            <Box sx={{ flex: 1, px: 2, py: 0.75, borderLeft: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <AccessTimeIcon sx={{ fontSize: 13, color: 'text.disabled' }} />
                                <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5, fontSize: 10 }}>
                                    Gripp uren
                                </Typography>
                            </Box>
                        </Box>

                        {/* Dag volledig geschreven → compact overzicht i.p.v. het rijen-rommeltje */}
                        {daySuggestions.length > 0 && daySuggestions.every(s => s.written || !s.approved) ? (
                            <Box sx={{ px: 2, py: 1.5, bgcolor: '#f0fdf4' }}>
                                {daySuggestions.filter(s => s.written).map(s => (
                                    <Typography key={s.id} variant="caption" sx={{ display: 'block', color: '#166534' }}>
                                        ✔ {s.hours}u — {s.projectLineName || s.projectName || 'Gripp'}{s.description ? ` · ${s.description}` : ''}
                                    </Typography>
                                ))}
                                {(() => {
                                    const written = daySuggestions.filter(s => s.written).reduce((sum, s) => sum + (s.hours || 0), 0) + dayExisting;
                                    const target = dagnorm;
                                    if (geenWerk.has(day)) return null;
                                    return (
                                        <Typography variant="caption" sx={{ display: 'block', mt: 0.5, fontWeight: 800, color: written >= target * 0.95 ? '#166534' : '#b45309' }}>
                                            Totaal {Math.round(written * 100) / 100}u van {target}u {written >= target * 0.95 ? '— dag compleet ✓' : `— nog ${Math.round((target - written) * 100) / 100}u te schrijven`}
                                        </Typography>
                                    );
                                })()}
                            </Box>
                        ) : (
                        /* Paired rows — desktop: 2 kolommen naast elkaar, mobiel: gestapeld */
                        pairedRows.map(({ task, sug }, i) => (
                            <Box key={sug?.id || task?.id || i} sx={{
                                display: 'grid', gridTemplateColumns: { xs: '1fr', md: `${AGENDA_KOLOM} 1fr` },
                                borderBottom: '1px solid', borderColor: 'divider', minHeight: 48,
                            }}>
                                {/* LEFT: agenda item (mobiel: alleen tonen als er echt een taak is) */}
                                <Box sx={{
                                    px: 2, py: 0.75, bgcolor: '#fcfcfd', alignItems: 'center', overflow: 'hidden',
                                    display: { xs: task ? 'flex' : 'none', md: 'flex' },
                                }}>
                                    {task ? <AgendaItem task={task} /> : (
                                        <Typography variant="caption" color="text.disabled" sx={{ fontStyle: 'italic' }}>—</Typography>
                                    )}
                                </Box>
                                {/* RIGHT: suggestion */}
                                <Box sx={{
                                    minWidth: 0, display: 'flex', alignItems: 'center',
                                    borderLeft: { xs: 0, md: '1px solid' }, borderColor: 'divider',
                                }}>
                                    {sug ? (
                                        <SuggestionRow suggestion={sug}
                                            projectOptions={projectOptions} projectLines={projectLines}
                                            onToggle={toggleApproval}
                                            onUpdate={updateSuggestion}
                                            onDelete={removeSuggestion}
                                            onProjectSelect={handleProjectSelect} />
                                    ) : task ? (() => {
                                        // Geen voorstel bij deze afspraak: "+ uren" + (als de server het weet) waarom niet.
                                        const reden = redenZonderVoorstel(task, data, daySuggestions);
                                        return (
                                            <Box sx={{ px: 2, py: 0.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <Button size="small" onClick={() => urenVoorItem(day, task)}
                                                    disabled={Boolean(plusBezig) || writing}
                                                    aria-label={`Uren toevoegen voor ${task.name || 'deze afspraak'}`}
                                                    startIcon={plusBezig === task.id ? <CircularProgress size={12} /> : null}
                                                    sx={{ textTransform: 'none', fontSize: 11.5, fontWeight: 600, color: 'text.secondary', minHeight: 26, py: 0, px: 1 }}>
                                                    + uren
                                                </Button>
                                                {reden && (
                                                    <Tooltip title={reden} arrow enterTouchDelay={0} leaveTouchDelay={4000}>
                                                        <InfoOutlinedIcon tabIndex={0} aria-label={reden} sx={{ fontSize: 15, color: 'text.disabled', cursor: 'help' }} />
                                                    </Tooltip>
                                                )}
                                            </Box>
                                        );
                                    })() : (
                                        <Box sx={{ px: 2, py: 0.75, display: 'flex', alignItems: 'center' }}>
                                            <Typography variant="caption" color="text.disabled">—</Typography>
                                        </Box>
                                    )}
                                </Box>
                            </Box>
                        )))}

                        {/* Add row */}
                        <Box sx={{ display: 'flex' }}>
                            <Box sx={{ flex: `0 0 ${AGENDA_KOLOM}`, display: { xs: 'none', md: 'block' } }} />
                            <Box sx={{ flex: 1, px: 2, py: 0.5, borderLeft: { xs: 0, md: '1px solid' }, borderColor: 'divider' }}>
                                <Button size="small" startIcon={<AddIcon />} onClick={() => addSuggestion(day)}
                                    sx={{ color: 'text.secondary', textTransform: 'none', fontSize: 11 }}>
                                    Regel toevoegen
                                </Button>
                            </Box>
                        </Box>
                        </>)}
                    </Paper>
                );
            })}

            {/* Zwevende dagkop (werkt als sticky): de kop van de dag waarin je werkt blijft bovenaan staan, met
                dezelfde inhoud; de volgende dag duwt hem omhoog weg. Dichte achtergrond, zodat niets doorschijnt. */}
            {data && zwevend && (
                <Paper elevation={2} square data-testid="zwevende-dagkop" sx={{
                    position: 'fixed', top: zwevend.top, left: zwevend.left, width: zwevend.width, zIndex: 1050,
                    bgcolor: 'background.paper', overflow: 'hidden', borderRadius: '0 0 6px 6px',
                }}>
                    <Box onClick={() => naarDag(zwevend.day)} sx={{ cursor: 'pointer', ...dagKopStijl(samenvattingVan(zwevend.day)) }}>
                        {dagKopInhoud(zwevend.day, samenvattingVan(zwevend.day))}
                    </Box>
                </Paper>
            )}

            {/* Summary + submit */}
            {data && suggestions.length > 0 && (
                <Paper sx={{ p: 3, mt: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Box>
                            <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                {totalApproved}h / {contractHours}h
                                <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                                    {hoursRemaining > 0.5 ? `(${hoursRemaining}h resterend)` :
                                     hoursRemaining < -0.5 ? `(${rond(Math.abs(hoursRemaining))}h over)` : ''}
                                </Typography>
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Chip label={`${openRegels.length} te schrijven`} size="small"
                                sx={{ bgcolor: 'success.light', color: 'success.main', fontWeight: 600 }} />
                            {suggestions.filter(s => !s.approved).length > 0 && (
                                <Chip label={`${suggestions.filter(s => !s.approved).length} afgekeurd`} size="small"
                                    sx={{ bgcolor: '#f1f4f5', color: 'text.secondary' }} />
                            )}
                        </Box>
                    </Box>

                    {data.warnings?.length > 0 && (
                        <Alert severity="warning" sx={{ mb: 2 }}>
                            {data.warnings.map((w, i) => <div key={i}>{w}</div>)}
                        </Alert>
                    )}

                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <Button variant="contained" size="large"
                            startIcon={writing ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                            onClick={writeToGripp} disabled={writing || openRegels.length === 0}
                            sx={{ flex: 1 }}>
                            {writing ? 'Schrijven...' : openRegels.length === 0
                                ? (suggestions.some(s => s.written) ? 'Alles geschreven' : 'Niets om te schrijven')
                                : `Schrijf ${openRegels.length} regel${openRegels.length === 1 ? '' : 's'} naar Gripp`}
                        </Button>
                        <Button variant="outlined" startIcon={<AutoFixHighIcon />}
                            onClick={generate} disabled={loading}>
                            Opnieuw
                        </Button>
                    </Box>
                </Paper>
            )}

            <Snackbar open={!!snackbar} autoHideDuration={4000} onClose={() => setSnackbar(null)} message={snackbar} />

            {/* Fout bij Schrijf dag / Schrijf alles: midden in beeld, met de weg terug naar de dag. */}
            <Dialog open={Boolean(schrijfFout)} onClose={() => setSchrijfFout(null)} maxWidth="xs" fullWidth
                aria-labelledby="schrijffout-titel" data-testid="schrijffout">
                <DialogTitle id="schrijffout-titel" sx={{ fontWeight: 700, fontSize: 17 }}>{schrijfFout?.titel}</DialogTitle>
                <DialogContent>
                    {(schrijfFout?.dagen || []).length > 0 ? (<>
                        <Typography variant="body2" sx={{ mb: 1.5 }}>
                            Boven {DAGGRENS_INTERN} uur op een dag mag alleen als alle uren extern zijn. Er is niets naar Gripp geschreven.
                        </Typography>
                        {schrijfFout.dagen.map((o) => (
                            <Box key={o.day} sx={{ mb: 1.25 }}>
                                <Typography variant="body2" sx={{ fontWeight: 700 }}>{formatDayShort(o.day)}</Typography>
                                <Typography variant="body2">{komma(o.totaal)} u totaal, waarvan {komma(o.intern)} u intern.</Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Haal minstens {komma(teVeelIntern(o))} u van de interne regels af, of boek ze op een extern project.
                                </Typography>
                            </Box>
                        ))}
                    </>) : (
                        <Typography variant="body2">{schrijfFout?.tekst}</Typography>
                    )}
                </DialogContent>
                <DialogActions sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                    {(schrijfFout?.dagen || []).map((o) => (
                        <Button key={o.day} variant="contained" size="small" onClick={() => naarDag(o.day)}>
                            Naar {formatDayShort(o.day)}
                        </Button>
                    ))}
                    <Button size="small" onClick={() => setSchrijfFout(null)}>Sluiten</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}


// ── Suggestion Row Component ───────────────────────────────────────────────

const SuggestionRow = memo(function SuggestionRow({ suggestion: s, projectOptions, projectLines, onToggle, onUpdate, onDelete, onProjectSelect }) {
    const unmapped = !s.projectLineId;
    // "+ uren"-regel met een herkend project hoeft niet open; zonder project staat het potloodje open.
    const [expanded, setExpanded] = useState((s.isNew && !s.gemist) || unmapped);
    // Na schrijven automatisch inklappen — het overzicht komt terug.
    useEffect(() => { if (s.written) setExpanded(false); }, [s.written]);
    const isInternalSuggestion = s.projectNumber === 'Intern' || s.projectName === 'Intern' ||
        projectOptions.some(p => p.isInternal && (
            String(p.projectId) === String(s.projectNumber) ||
            String(p.lookupProjectId) === String(s.projectNumber)
        ));
    const isOfferte = isOfferteSleutel(s.projectNumber);
    const lineKey = projectLines[s.projectNumber] ? s.projectNumber : (isInternalSuggestion && projectLines.Intern ? 'Intern' : s.projectNumber);
    const lines = projectLines[lineKey] || [];
    const rejected = !s.approved;
    const written = s.written;

    // Gripp project search
    const [searchResults, setSearchResults] = useState([]);
    const [searching, setSearching] = useState(false);
    const [showArchived, setShowArchived] = useState(false);
    const [projectSearchTerm, setProjectSearchTerm] = useState('');
    // Laatst gekozen optie: blijft de waarde van het veld, ook als hij uit de zoekresultaten valt
    // (bijvoorbeeld een ouder project na het uitzetten van Archief tonen). Wissen (null) verandert het voorstel
    // niet (handleProjectSelect negeert null), dus de cache ook niet: veld en voorstel blijven gelijk.
    const [gekozenUitLijst, setGekozenUitLijst] = useState(null);
    const searchTimer = useRef(null);
    // Seq-guard: alleen het láátste zoekresultaat mag de lijst zetten — voorkomt
    // out-of-order responses bij snel typen of archief-toggle tijdens een zoekactie.
    const searchSeq = useRef(0);

    useEffect(() => () => clearTimeout(searchTimer.current), []);

    const runSearch = (value, includeArchived) => {
        clearTimeout(searchTimer.current);
        searchTimer.current = setTimeout(async () => {
            const seq = ++searchSeq.current;
            setSearching(true);
            try {
                // Default to active projects; the row toggle can include archived projects.
                // Open offertes zoeken we ernaast (20 okt 2026): uren voor een offerte horen op die offerte.
                const offertesZoeken = authFetch(`${BACKEND_URL}/Api/slim-uren/offertes?zoek=${encodeURIComponent(value)}`)
                    .then((r) => (r.ok ? r.json() : { offertes: [] })).catch(() => ({ offertes: [] }));
                const res = await authFetch(`${BACKEND_URL}/Api/gripp/projects?search=${encodeURIComponent(value)}&activeOnly=${includeArchived ? 'false' : 'true'}`);
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const data = await res.json();
                const offertes = ((await offertesZoeken).offertes || []).map((o) => offerteOptie({ ...o, sourceGroup: 'Offertes' }));
                if (seq !== searchSeq.current) return; // verouderd resultaat — negeren
                setSearchResults(zoekOpties(data.projects, offertes, includeArchived));
            } catch (err) {
                if (seq !== searchSeq.current) return;
                console.error('[SlimUren] Project search failed:', err);
                toast.error('Zoeken mislukt: ' + (err.message || 'onbekende fout'));
                setSearchResults([]);
            }
            if (seq === searchSeq.current) setSearching(false);
        }, 400);
    };

    const handleSearchInput = (_, value, reason) => {
        if (reason === 'reset') return;
        setProjectSearchTerm(value || '');
        if (!value || value.length < 2) { setSearchResults([]); return; }
        runSearch(value, showArchived);
    };

    const toggleArchivedProjects = () => {
        const next = !showArchived;
        setShowArchived(next);
        if (projectSearchTerm.length >= 2) runSearch(projectSearchTerm, next);
    };

    // Combine recent + search results, deduplicated
    const visibleProjectOptions = projectOptions.filter(p => showArchived || !p.archived);
    // Typ je een zoekterm, dan staan de zoekresultaten BOVENAAN en verdwijnen 'Snel kiezen'/'Recent'
    // tenzij ze zelf matchen — anders vielen treffers onder de vouw weg (wiki-feedback 18 sep).
    const zoekt = (projectSearchTerm || '').trim().length >= 2;
    const term = (projectSearchTerm || '').trim().toLowerCase();
    const matchtTerm = (p) => !zoekt || `${p.projectNumber || ''} ${p.projectName || ''} ${p.companyName || ''} ${p.label || ''}`.toLowerCase().includes(term);
    const allProjectOptions = [];
    if (zoekt) {
        const bekend = (x) => allProjectOptions.find(y => String(y.projectId) === String(x.projectId));
        for (const sr of searchResults) { if (!sr.ouder) allProjectOptions.push(sr); }
        for (const p of visibleProjectOptions) {
            if (matchtTerm(p) && !bekend(p)) allProjectOptions.push(p);
        }
        // "Ouder" als laatste kop (alleen met Archief tonen); je eigen recente projecten staan al onder "Recent".
        for (const sr of searchResults) { if (sr.ouder && showArchived && !bekend(sr)) allProjectOptions.push(sr); }
    } else {
        allProjectOptions.push(...visibleProjectOptions);
        for (const sr of searchResults) {
            if (!showArchived && (sr.archived || sr.ouder)) continue;
            if (!allProjectOptions.find(p => String(p.projectId) === String(sr.projectId))) allProjectOptions.push(sr);
        }
    }
    // Het gekozen project zoeken we in ALLE bekende opties, niet alleen in de zichtbare lijst: zodra je typt
    // verdwijnen 'Snel kiezen'/'Recent' uit de lijst en werd de waarde null → MUI reset dan het invoerveld
    // (reason 'reset') en je getypte tekst verdween. Bug gemeld door Joris, 21 sep 2026.
    const alleBekend = [...allProjectOptions, ...projectOptions, ...searchResults, ...(gekozenUitLijst ? [gekozenUitLijst] : [])];
    const selectedProject = alleBekend.find(p =>
        String(p.projectNumber) === String(s.projectNumber) ||
        String(p.projectId) === String(s.projectNumber) ||
        (p.isInternal && isInternalSuggestion)
    ) || null;
    // Een offerte als doel (offerte:<id>) staat meestal niet in 'Recent' of de zoekresultaten: maak de optie uit het voorstel zelf.
    const gekozenOptie = selectedProject || (isOfferte ? offerteOptie({ projectNumber: s.projectNumber, projectName: s.projectName, sourceGroup: 'Gekozen offerte' }) : null);
    if (gekozenOptie && !allProjectOptions.some(p => String(p.projectId || p.projectNumber) === String(gekozenOptie.projectId || gekozenOptie.projectNumber))) {
        // Gekozen waarde blijft een geldige optie, ook tijdens het zoeken. Bij zijn eigen kop invoegen (MUI groupBy
        // wil aaneengesloten koppen), anders vóór "Ouder" zodat die de laatste kop blijft.
        const groep = gekozenOptie.sourceGroup || 'Projecten';
        const laatsteVanGroep = allProjectOptions.map((p) => p.sourceGroup || 'Projecten').lastIndexOf(groep);
        const eersteOuder = allProjectOptions.findIndex((p) => p.sourceGroup === 'Ouder');
        const plek = laatsteVanGroep >= 0 ? laatsteVanGroep + 1 : (eersteOuder >= 0 ? eersteOuder : allProjectOptions.length);
        allProjectOptions.splice(plek, 0, gekozenOptie);
    }
    const internalOption = allProjectOptions.find(p => p.isInternal);
    const internalLineKey = internalOption?.projectNumber || 'Intern';
    const internalLines = (projectLines[internalLineKey] || projectLines.Intern || [])
        .slice()
        .sort((a, b) => {
            const aName = (a.name || a.searchname || '').toLowerCase();
            const bName = (b.name || b.searchname || '').toLowerCase();
            const aRank = INTERNAL_LINE_PRIORITY.findIndex(key => aName.includes(key));
            const bRank = INTERNAL_LINE_PRIORITY.findIndex(key => bName.includes(key));
            return (aRank === -1 ? 99 : aRank) - (bRank === -1 ? 99 : bRank);
        })
        .slice(0, 40);

    return (
        <Box sx={{
            px: 2, py: 1, width: '100%',
            opacity: rejected ? 0.5 : (written ? 0.6 : 1),
            bgcolor: written ? 'success.light' : (rejected ? '#fafafa' : (unmapped ? '#fef8ee' : 'transparent')),
            '&:hover': { bgcolor: written ? 'success.light' : (rejected ? '#fafafa' : '#f8f9fa') },
            transition: 'all 0.15s',
        }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Checkbox checked={s.approved} onChange={() => onToggle(s.id)} size="small"
                    sx={{ p: 0.5, color: 'text.secondary', '&.Mui-checked': { color: 'success.main' } }} />

                <UrenVeld waarde={s.hours} onChange={(n) => onUpdate(s.id, 'hours', n)} />
                <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>h</Typography>

                <Box onClick={() => setExpanded(!expanded)}
                    sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 0.5, overflow: 'hidden', cursor: 'pointer' }}>
                    {s.projectNumber && (
                        <Tooltip title={isOfferte ? (s.projectName || 'Offerte') : ''} arrow disableHoverListener={!isOfferte}>
                            <Chip label={isInternalSuggestion ? 'Intern' : (s.projectName || (isOfferte ? 'Offerte' : s.projectNumber))}
                                size="small" data-testid={isOfferte ? 'offerte-chip' : undefined}
                                sx={{ height: 20, fontSize: 11, fontWeight: 600, flexShrink: 0, maxWidth: isOfferte ? 170 : 120,
                                    bgcolor: isInternalSuggestion ? '#e8e4f2' : (isOfferte ? '#fdf3e1' : '#e8edf2'),
                                    color: isInternalSuggestion ? '#635983' : (isOfferte ? '#8a5a00' : undefined) }} />
                        </Tooltip>
                    )}
                    <Typography variant="body2" noWrap sx={{
                        flex: 1, textDecoration: rejected ? 'line-through' : 'none',
                        color: unmapped ? 'warning.main' : (rejected ? 'text.secondary' : 'text.primary'), fontSize: 13,
                        fontStyle: unmapped ? 'italic' : 'normal',
                    }}>
                        {s.projectLineName || (unmapped ? 'Selecteer project...' : s.projectName)}
                    </Typography>
                    {s.description && s.description !== s.projectLineName && (
                        <Typography variant="caption" noWrap sx={{ color: 'text.disabled', fontSize: 11, maxWidth: 150 }}>
                            {s.description}
                        </Typography>
                    )}
                    <SourceBadge source={s.source} geleerd={s.geleerd} />
                </Box>

                <IconButton size="small" onClick={() => setExpanded(!expanded)} sx={{ opacity: 0.5 }}>
                    <EditIcon sx={{ fontSize: 16 }} />
                </IconButton>
                <IconButton size="small" onClick={() => onDelete(s.id)}
                    sx={{ opacity: 0.4, '&:hover': { color: 'error.main', opacity: 1 } }}>
                    <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                </IconButton>
            </Box>

            {/* Bronmails (📧-voorstel): klik op een chip om te zien welke mail je stuurde. */}
            {s.source === 'mail' && <MailBronnen links={s.links} />}

            <Collapse in={expanded}>
                <Box sx={{ pl: 5, pr: 1, py: 1.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {internalOption && internalLines.length > 0 && (
                        <Box>
                            <Typography variant="caption" sx={{ display: 'block', mb: 0.75, color: 'text.secondary', fontWeight: 700 }}>
                                Interne uren
                            </Typography>
                            <TagKeuze
                                opties={opVoorkeur(internalLines, (l) => l.name || l.searchname)
                                    .map((l) => ({ id: l.id, label: l.name || l.searchname || `Line ${l.id}`, ruw: l }))}
                                actiefId={s.projectLineId}
                                onKies={(o) => { onthoudKeuze(o.label); onProjectSelect(s.id, internalOption, o.ruw); setExpanded(false); }}
                                kleur={{ bg: '#e8e4f2', fg: '#635983', rand: '#bdb4d6' }} />
                        </Box>
                    )}

                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700 }}>
                            Project zoeken
                        </Typography>
                        <Chip
                            label={showArchived ? 'Archief zichtbaar' : 'Archief tonen'}
                            clickable
                            onClick={toggleArchivedProjects}
                            size="small"
                            variant={showArchived ? 'filled' : 'outlined'}
                            sx={{
                                height: 22, fontSize: 10, fontWeight: 700,
                                bgcolor: showArchived ? '#fff7ed' : '#fff',
                                color: showArchived ? '#c2410c' : 'text.secondary',
                                borderColor: showArchived ? '#fed7aa' : 'divider',
                            }}
                        />
                    </Box>
                    <Autocomplete options={allProjectOptions}
                        getOptionLabel={o => o.label || o.projectName || ''}
                        value={gekozenOptie}
                        onChange={(_, val) => { if (val) setGekozenUitLijst(val); onProjectSelect(s.id, val); }}
                        onInputChange={handleSearchInput}
                        loading={searching}
                        groupBy={option => option.sourceGroup || 'Projecten'}
                        openOnFocus
                        selectOnFocus
                        handleHomeEndKeys
                        noOptionsText={projectSearchTerm.length < 2 ? 'Typ minimaal 2 letters om te zoeken' : (showArchived ? 'Geen projecten gevonden' : 'Geen actieve projecten gevonden. Ouder project? Zet Archief tonen aan.')}
                        renderInput={(params) => <TextField {...params} label="Project" placeholder="Zoek op project of klant" size="small" variant="outlined" />}
                        renderOption={(props, option) => {
                            const statusColor = {
                                'Lopend': '#10b981', 'Actief': '#10b981',
                                'Concept': '#6366f1', 'Preproductie': '#6366f1',
                                'Te factureren': '#f59e0b', 'Afwerking': '#f59e0b',
                                'Wachten op': '#f97316', 'Gepauzeerd': '#94a3b8',
                                'Afgerond': '#6b7280', 'Archief': '#9ca3af', 'Intern': '#635983', 'Offerte': '#b7791f',
                            }[option.statusLabel] || '#94a3b8';
                            return (
                                <li {...props} key={option.projectId || option.projectNumber}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', gap: 1 }}>
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Typography variant="body2" sx={{ fontSize: 13 }}>{option.projectName}</Typography>
                                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10, display: 'block' }}>
                                                {option.companyName ? `${option.companyName} · ` : ''}{option.isOfferte ? `Offerte${option.offerteNummer ? ` #${option.offerteNummer}` : ''} — uren op de offerte` : `Gripp #${option.projectNumber}`}
                                            </Typography>
                                        </Box>
                                        {option.statusLabel && (
                                            <Chip
                                                label={option.statusLabel}
                                                size="small"
                                                sx={{ height: 18, fontSize: 10, fontWeight: 600, bgcolor: `${statusColor}22`, color: statusColor, flexShrink: 0 }}
                                            />
                                        )}
                                    </Box>
                                </li>
                            );
                        }}
                        isOptionEqualToValue={(opt, val) => String(opt.projectId || opt.projectNumber) === String(val?.projectId || val?.projectNumber)}
                        size="small" filterOptions={x => x}
                        slotProps={{ popper: { sx: { zIndex: 2000 } } }}
                        ListboxProps={{ style: { maxHeight: 320 } }}
                        sx={{ '& .MuiInputBase-root': { fontSize: 13 } }} />

                    {lines.length > 0 && (
                        <Box>
                            <Typography variant="caption" sx={{ display: 'block', mb: 0.75, color: 'text.secondary', fontWeight: 700 }}>
                                Projectlijn
                            </Typography>
                            <TagKeuze
                                opties={opVoorkeur(lines, (l) => l.name || l.additionalsubject)
                                    .map((l) => ({ id: l.id, label: l.name || l.additionalsubject || `Line ${l.id}`, ruw: l }))}
                                actiefId={s.projectLineId}
                                onKies={(o) => {
                                    onthoudKeuze(o.label);
                                    onUpdate(s.id, 'projectLineId', o.ruw.id || null);
                                    onUpdate(s.id, 'projectLineName', o.ruw.name || o.ruw.additionalsubject || '');
                                    setExpanded(false);
                                }}
                                kleur={{ bg: '#e8edf2', fg: '#33566f', rand: '#b9cbd8' }} />
                        </Box>
                    )}

                    <TextField label="Beschrijving" value={s.description || ''}
                        onChange={e => onUpdate(s.id, 'description', e.target.value)}
                        size="small" variant="outlined" InputProps={{ sx: { fontSize: 13 } }} />
                </Box>
            </Collapse>
        </Box>
    );
});
